import ExcelJS from "exceljs";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import html2pdf from "html2pdf.js";
import { saveAs } from "file-saver";
import styles from "./CreditMapGenerator.module.css";
import axiosInstance from "../../Fbaseurl";
import NavDownloadArrow from "../../assets/NavDownloadArrow.svg";
import DownloadShape from "../../assets/DownloadShape.svg";
import Union from "../../assets/Union.svg";
import NavUploadArrow from "../../assets/NavUploadArrow.svg";
import FileIcon from "../../assets/FileIcon.svg";
import DeleteIcon from "../../assets/DeleteIcon.svg";
import ArrowUpload from "../../assets/ArrowUpload.svg";
import MaskedIcon from "../../assets/MaskedIcon.svg";
import CSV from "../../assets/CSV.svg";
import PDF from "../../assets/PDF.svg";
import Person from "../../assets/Person.svg";
import AnnualHires from "../../assets/AnnualHires.svg";
import canaryLogo from "../../assets/Logo.svg";
import { showToast } from "../../store/reducers/ToastMessageReducer";
import { useDispatch, useSelector } from "react-redux";
import MapView from "./MapView";
import LoadingButton from "@mui/lab/LoadingButton";
import { display } from "@mui/system";
import Checkbox from "../components/CommonComponents/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../components/CommonComponents/Button";
import {
  convertJsonToExcel,
  prepareRequiredJsonToExcelDownload,
  resolveVersionHeading,
  buildVersionedFileName,
  CATEGORY_EXPORT_COLUMNS,
  CATEGORY_DEFAULT_KEY,
  initializeCategoryTotals,
  resolveCategoryColumnKey,
} from "../SalesPersonCommonMethods/taxCreditsReport";
import { useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import stripHtml from "../../CommonUtils/htmlStrip";

import { openPrintPreviewFromTemplate } from "../../CommonUtils/printPreview";
import {
  setCreditMapResults,
  clearCreditMapResults,
} from "../../store/actions/resultsCacheActions";
import {
  computeCreditTypeTotals,
  CREDIT_BUCKET_KEYS,
  CREDIT_TYPE_KEYS,
  resolveCreditTypeKey,
  resolveCreditTypeFromRecord,
} from "../../CommonUtils/taxCreditTotals";
import {
  EMPOWERMENT_FILTER_VALUES,
  EMPOWERMENT_ZONE_TYPES,
  getEmpowermentZoneCategories,
  getEmpowermentZoneCategory,
  getEmpowermentZoneLabels,
} from "../../CommonUtils/empowermentZones";
import { setCreditMapBusy } from "../../store/reducers/SalespersonReducer";
import { buildGraphHtml, downloadMapAsHtml, downloadMapAsPdf, downloadGraphAsPdf, downloadFullAnalysisPdf, renderMapCanvas, renderGraphCanvas, composeWithHeader } from "../../CommonUtils/viewDownloads";

const STATIC_MAP_API_KEY = process.env.REACT_APP_MAPS_API_KEY;

const CATEGORY_META_LOOKUP = CATEGORY_EXPORT_COLUMNS.reduce((acc, column) => {
  acc[column.key] = column;
  return acc;
}, {});

const TAX_CREDIT_FILTERS = {
  ALL: "all",
  FEDERAL_WOTC: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  FEDERAL_NON_WOTC: CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
  STATE: CREDIT_TYPE_KEYS.STATE,
};

const CREDIT_TOTAL_DISPLAY = {
  ALL: "all",
  FEDERAL_WOTC: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  FEDERAL_NON_WOTC: CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
  STATE: CREDIT_TYPE_KEYS.STATE,
};
const TAX_CREDIT_FILTER_LABELS = {
  [TAX_CREDIT_FILTERS.ALL]: "All Credits",
  [TAX_CREDIT_FILTERS.FEDERAL_WOTC]: "Federal WOTC",
  [TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC]: "Federal (Non-WOTC)",
  [TAX_CREDIT_FILTERS.STATE]: "State Credits",
};
const resolveCreditFilterLabel = (value) =>
  TAX_CREDIT_FILTER_LABELS[value] || TAX_CREDIT_FILTER_LABELS[TAX_CREDIT_FILTERS.ALL];
const FEDERAL_FILTER_VALUES = [
  TAX_CREDIT_FILTERS.FEDERAL_WOTC,
  TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC,
];
const CREDIT_SCOPE_VALUE_SET = new Set(Object.values(CREDIT_TOTAL_DISPLAY));
const CREDIT_FILTER_VALUE_SET = new Set([
  ...FEDERAL_FILTER_VALUES,
  TAX_CREDIT_FILTERS.STATE,
]);
const ZONE_LABELS = {
  [EMPOWERMENT_ZONE_TYPES.EMPOWERMENT]: "Federal Empowerment Zones",
  [EMPOWERMENT_ZONE_TYPES.RURAL]: "Rural Renewal Counties",
  [EMPOWERMENT_ZONE_TYPES.NONE]: "No Zone Designation",
};
const ZONE_COLORS = {
  [EMPOWERMENT_ZONE_TYPES.EMPOWERMENT]: "#1a9c4b",
  [EMPOWERMENT_ZONE_TYPES.RURAL]: "#d97706",
  [EMPOWERMENT_ZONE_TYPES.NONE]: "#4c6ef5",
};
const NAICS_COLOR_PALETTE = [
  "#4c6ef5",
  "#0ea5e9",
  "#14b8a6",
  "#f59e0b",
  "#ec4899",
  "#6366f1",
];
const buildPieBackgroundFromSegments = (segments = []) => {
  const positive = (Array.isArray(segments) ? segments : []).filter(
    (segment) => segment.percent > 0
  );
  if (!positive.length) return "#e4e7f1";
  if (positive.length === 1) return positive[0].color;
  let current = 0;
  const stops = positive.map((segment) => {
    const start = current;
    current += segment.percent;
    return `${segment.color} ${start}% ${Math.min(current, 100)}%`;
  });
  if (current < 100) {
    stops.push(`#e4e7f1 ${current}% 100%`);
  }
  return `conic-gradient(${stops.join(", ")})`;
};

const resolveScopeFromFilter = (filterValue = TAX_CREDIT_FILTERS.ALL) => {
  if (filterValue === TAX_CREDIT_FILTERS.ALL) {
    return CREDIT_TOTAL_DISPLAY.ALL;
  }
  if (CREDIT_SCOPE_VALUE_SET.has(filterValue)) {
    return filterValue;
  }
  return CREDIT_TOTAL_DISPLAY.ALL;
};

const resolveFilterFromScope = (scopeValue = CREDIT_TOTAL_DISPLAY.ALL) => {
  if (scopeValue === CREDIT_TOTAL_DISPLAY.STATE) {
    return TAX_CREDIT_FILTERS.STATE;
  }
  if (scopeValue === CREDIT_TOTAL_DISPLAY.FEDERAL_WOTC) {
    return TAX_CREDIT_FILTERS.FEDERAL_WOTC;
  }
  if (
    scopeValue === CREDIT_TOTAL_DISPLAY.FEDERAL_NON_WOTC ||
    scopeValue === "federal"
  ) {
    return TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC;
  }
  return TAX_CREDIT_FILTERS.ALL;
};

const NAICS_INPUT_PATTERN = /^[0-9]{0,6}$/;
const NAICS_FILTER_PATTERN = /^[0-9]{2,6}$/;
const BUSINESS_NAICS_FIELDS = [
  "preferredNaics",
  "naics",
  "naicS01",
  "naicS02",
  "naicS03",
];

const CREDIT_AMOUNT_FIELDS = [
  "estimatedCreditAmount",
  "estimatedAmount",
  "projectedAmount",
  "projectedTaxCreditAmount",
  "creditAmount",
  "amount",
  "value",
];

const normalizeName = (value) =>
  stripHtml(value || "")
    .trim()
    .toLowerCase();

const normalizeTextValue = (value) =>
  typeof value === "string" || typeof value === "number"
    ? normalizeName(String(value))
    : "";

const resolveEntryFilterType = (label, entry = null) => {
  if (entry && typeof entry === "object") {
    const typeFromId = resolveCreditTypeFromRecord(entry);
    if (
      typeFromId &&
      typeFromId !== CREDIT_TYPE_KEYS.STATE &&
      CREDIT_FILTER_VALUE_SET.has(typeFromId)
    ) {
      return typeFromId;
    }
  }
  const resolved = resolveCreditTypeKey(label);
  if (resolved && CREDIT_FILTER_VALUE_SET.has(resolved)) {
    return resolved;
  }
  return TAX_CREDIT_FILTERS.STATE;
};

const initializeCreditTotals = () =>
  CREDIT_BUCKET_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});

const normalizeCodeValue = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim().toLowerCase();
};

const normalizeNaicsDigits = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).replace(/[^0-9]/g, "").slice(0, 6);
};

const collectBusinessCodes = (business = {}, fieldNames = []) => {
  const codes = [];
  const pushValue = (value) => {
    if (value === undefined || value === null) return;
    const strValue = String(value);
    strValue
      .split(/[,;|]/)
      .map((segment) => segment.trim())
      .filter(Boolean)
      .forEach((segment) => {
        const normalized = normalizeCodeValue(segment);
        if (normalized) codes.push(normalized);
      });
  };
  fieldNames.forEach((field) => {
    const data = business?.[field];
    if (Array.isArray(data)) {
      data.forEach(pushValue);
    } else {
      pushValue(data);
    }
  });
  return codes;
};

const pickApiValue = (source, fieldNames = []) => {
  if (!source || !Array.isArray(fieldNames)) return undefined;
  for (const field of fieldNames) {
    if (
      field &&
      Object.prototype.hasOwnProperty.call(source, field) &&
      source[field] !== undefined &&
      source[field] !== null
    ) {
      const value = source[field];
      if (String(value).trim() !== "") {
        return value;
      }
    }
  }
  return undefined;
};

const sanitizeNaicsDigits = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).replace(/[^0-9]/g, "");
};

const getPrimaryNaicsCode = (business = {}) => {
  const codes = collectBusinessCodes(business, BUSINESS_NAICS_FIELDS);
  for (const code of codes) {
    const digits = sanitizeNaicsDigits(code);
    if (digits.length >= 2) return digits.slice(0, 6);
  }
  return "";
};

const doesBusinessMatchCodes = (
  business,
  filterValues = [],
  businessFields = [],
  options = {}
) => {
  const normalizedFilters = (Array.isArray(filterValues) ? filterValues : [])
    .map((value) => normalizeCodeValue(value))
    .filter(Boolean);
  if (!normalizedFilters.length) return false;
  const businessCodes = collectBusinessCodes(business, businessFields);
  if (!businessCodes.length) return false;
  if (options.allowPrefixMatch) {
    return businessCodes.some((code) =>
      normalizedFilters.some((filter) => code.startsWith(filter))
    );
  }
  const filterSet = new Set(normalizedFilters);
  return businessCodes.some((code) => filterSet.has(code));
};

const extractTaxCreditFields = (post) => {
  if (
    typeof post?.projectedCredit !== "undefined" &&
    typeof post?.eligibleCredits !== "undefined"
  ) {
    return {
      projectedCredit: post.projectedCredit,
      eligibleCredits: post.eligibleCredits,
    };
  }
  let projectedCredit;
  let eligibleCredits = [];
  if (Array.isArray(post?.taxCredits) && post.taxCredits.length > 0) {
    projectedCredit = post.taxCredits[0]?.totalProjectedTaxCreditAmount;
    eligibleCredits =
      post.taxCredits[0]?.availableTaxCredits?.map((c) => c.name) || [];
  }
  return {
    projectedCredit,
    eligibleCredits,
  };
};

const normalizeCreditLabel = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value).trim();
  return "";
};

const normalizeCreditCategoryLabel = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value).trim();
  return "";
};

const buildCategoryKey = (value) => normalizeTextValue(value);

const parseCreditAmountValue = (value) => {
  if (value === null || typeof value === "undefined") return null;
  if (typeof value === "number") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]+/g, "");
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const getCreditAmountFromEntry = (entry) => {
  if (!entry || typeof entry === "string") return null;
  for (const field of CREDIT_AMOUNT_FIELDS) {
    if (
      Object.prototype.hasOwnProperty.call(entry, field) &&
      entry[field] !== null &&
      typeof entry[field] !== "undefined"
    ) {
      const parsed = parseCreditAmountValue(entry[field]);
      if (parsed !== null) {
        return parsed;
      }
    }
  }
  return null;
};

const normalizeCreditEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") {
    const label = normalizeCreditLabel(entry);
    if (!label) return null;
    return {
      label,
      type: resolveEntryFilterType(label),
      amount: null,
      category: null,
      categoryKey: "",
    };
  }
  if (typeof entry === "object") {
    const label =
      normalizeCreditLabel(entry.name) ||
      normalizeCreditLabel(entry.creditName) ||
      normalizeCreditLabel(entry.description);
    if (!label) return null;
    const resolvedCategory = normalizeCreditCategoryLabel(
      entry.category || entry.Category || null
    );
    return {
      label,
      type: resolveEntryFilterType(label, entry),
      amount: getCreditAmountFromEntry(entry),
      category: resolvedCategory || null,
      categoryKey: resolvedCategory ? buildCategoryKey(resolvedCategory) : "",
    };
  }
  return null;
};

const collectBusinessCategoryKeys = (business = {}, taxCreditLookup = {}) => {
  const entries = getBusinessCreditEntries(business, taxCreditLookup);
  const unique = new Map();
  entries.forEach((entry) => {
    const key = entry?.categoryKey;
    if (!key) return;
    if (!unique.has(key)) {
      unique.set(key, entry.category || "Uncategorized");
    }
  });
  if (Array.isArray(business?.creditCategories)) {
    business.creditCategories.forEach((label) => {
      const normalizedLabel = normalizeCreditCategoryLabel(label);
      if (!normalizedLabel) return;
      const key = buildCategoryKey(normalizedLabel);
      if (key && !unique.has(key)) {
        unique.set(key, normalizedLabel);
      }
    });
  }
  if (
    business?.precalculatedCategoryTotals &&
    typeof business.precalculatedCategoryTotals === "object"
  ) {
    Object.entries(business.precalculatedCategoryTotals).forEach(
      ([rawKey, rawValue]) => {
        const resolvedKey = resolveCategoryColumnKey(rawKey);
        if (!resolvedKey) return;
        const parsed = parseCreditAmountValue(rawValue);
        if (typeof parsed === "number" && parsed > 0) {
          const label = CATEGORY_META_LOOKUP[resolvedKey]?.label || rawKey;
          const key = buildCategoryKey(label);
          if (key && !unique.has(key)) {
            unique.set(key, label);
          }
        }
      }
    );
  }
  return Array.from(unique.entries()).map(([key, label]) => ({
    key,
    label,
  }));
};

const getBusinessCreditEntries = (business = {}, taxCreditLookup = {}) => {
  let sourceEntries = [];
  const taxCreditGroups = Array.isArray(business?.taxCredits)
    ? business.taxCredits
    : [];
  if (
    taxCreditGroups.length > 0 &&
    Array.isArray(taxCreditGroups[0]?.availableTaxCredits) &&
    taxCreditGroups[0].availableTaxCredits.length > 0
  ) {
    sourceEntries = taxCreditGroups[0].availableTaxCredits;
  } else if (
    business?.prospectBusinessId &&
    taxCreditLookup[business.prospectBusinessId]
  ) {
    const cached = taxCreditLookup[business.prospectBusinessId];
    if (Array.isArray(cached?.availableCredits) && cached.availableCredits.length) {
      sourceEntries = cached.availableCredits;
    } else if (Array.isArray(cached?.eligibleCredits) && cached.eligibleCredits.length) {
      sourceEntries = cached.eligibleCredits.map((entry) =>
        typeof entry === "string" ? { name: entry } : entry
      );
    } else {
      sourceEntries = [];
    }
  } else if (Array.isArray(business?.eligibleCredits)) {
    sourceEntries = business.eligibleCredits;
  }
  return (Array.isArray(sourceEntries) ? sourceEntries : [])
    .map((entry) => normalizeCreditEntry(entry))
    .filter(Boolean);
};

const getBusinessEligibleCreditNames = (
  business = {},
  taxCreditLookup = {}
) => {
  return getBusinessCreditEntries(business, taxCreditLookup).map(
    (entry) => entry.label
  );
};

const getBusinessProjectedCreditValue = (business = {}, taxCreditLookup = {}) => {
  if (
    business?.prospectBusinessId &&
    taxCreditLookup[business.prospectBusinessId]
  ) {
    const override = taxCreditLookup[business.prospectBusinessId];
    if (typeof override.projectedCredit !== "undefined") {
      const parsed = parseCreditAmountValue(override.projectedCredit);
      if (parsed !== null) return parsed;
    }
  }
  if (
    Array.isArray(business?.taxCredits) &&
    business.taxCredits.length > 0 &&
    typeof business.taxCredits[0]?.totalProjectedTaxCreditAmount !== "undefined"
  ) {
    const parsed = parseCreditAmountValue(
      business.taxCredits[0].totalProjectedTaxCreditAmount
    );
    if (parsed !== null) return parsed;
  }
  const fallback =
    typeof business.totalProjectedTaxCreditAmount !== "undefined"
      ? business.totalProjectedTaxCreditAmount
      : business.projectedCredit;
  const parsedFallback = parseCreditAmountValue(fallback);
  if (parsedFallback !== null) return parsedFallback;
  return null;
};

const buildCategoryBreakdown = (entries = [], business = {}) => {
  const totals = initializeCategoryTotals();
  const assignCategoryValue = (label, amount) => {
    if (!label) return;
    if (typeof amount !== "number" || Number.isNaN(amount) || amount <= 0) {
      return;
    }
    const resolvedKey = resolveCategoryColumnKey(label);
    if (resolvedKey) {
      totals[resolvedKey] += amount;
      return;
    }
    totals[CATEGORY_DEFAULT_KEY] += amount;
  };
  entries.forEach((entry) => {
    if (!entry || typeof entry.amount !== "number" || entry.amount <= 0) {
      return;
    }
    assignCategoryValue(entry.category, entry.amount);
  });
  if (
    business?.precalculatedCategoryTotals &&
    typeof business.precalculatedCategoryTotals === "object"
  ) {
    Object.entries(business.precalculatedCategoryTotals).forEach(
      ([rawKey, rawValue]) => {
        const resolvedKey = resolveCategoryColumnKey(rawKey);
        if (!resolvedKey) return;
        if (totals[resolvedKey] > 0) return;
        const fallbackValue = parseCreditAmountValue(rawValue);
        if (typeof fallbackValue === "number" && fallbackValue > 0) {
          totals[resolvedKey] = fallbackValue;
        }
      }
    );
  }
  return totals;
};

const buildCreditDisplayData = (business = {}, taxCreditLookup = {}) => {
  const entries = getBusinessCreditEntries(business, taxCreditLookup);
  const projectedCredit = getBusinessProjectedCreditValue(
    business,
    taxCreditLookup
  );
  if (!entries.length) {
    const fallbackTotal =
      typeof projectedCredit === "number" && !Number.isNaN(projectedCredit)
        ? projectedCredit
        : null;
    const hasProjectionValue =
      typeof fallbackTotal === "number" && !Number.isNaN(fallbackTotal);
    const categoryBreakdown = buildCategoryBreakdown([], business);
    const emptyTotals = initializeCreditTotals();
    if (hasProjectionValue) {
      emptyTotals[TAX_CREDIT_FILTERS.STATE] = fallbackTotal;
    }
    return {
      entries: [],
      totals: {
        federalWotc: emptyTotals[TAX_CREDIT_FILTERS.FEDERAL_WOTC],
        federalNonWotc: emptyTotals[TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC],
        state: emptyTotals[TAX_CREDIT_FILTERS.STATE],
        total: hasProjectionValue ? fallbackTotal : 0,
      },
      projectedCredit: hasProjectionValue ? fallbackTotal : null,
      hasProjectionValue,
      categoryBreakdown,
    };
  }
  let definedSum = 0;
  const enhancedEntries = entries.map((entry) => {
    const baseAmount =
      typeof entry.amount === "number" && entry.amount > 0 ? entry.amount : 0;
    if (baseAmount > 0) {
      definedSum += baseAmount;
    }
    return {
      label: entry.label,
      type: entry.type,
      amount: baseAmount,
      category: entry.category || null,
      categoryKey: entry.categoryKey || "",
    };
  });
  const normalizedProjected =
    typeof projectedCredit === "number" && !Number.isNaN(projectedCredit)
      ? projectedCredit
      : null;
  if (normalizedProjected !== null && enhancedEntries.length > 0) {
    const remainder = normalizedProjected - definedSum;
    if (remainder > 0) {
      const share = remainder / enhancedEntries.length;
      enhancedEntries.forEach((entry) => {
        entry.amount += share;
      });
    }
  }
  const categoryBreakdown = buildCategoryBreakdown(enhancedEntries, business);

  const totals = initializeCreditTotals();
  enhancedEntries.forEach((entry) => {
    if (typeof entry.amount === "number" && entry.amount > 0) {
      totals[entry.type] += entry.amount;
    }
  });
  const totalValue =
    normalizedProjected !== null
      ? normalizedProjected
      : CREDIT_BUCKET_KEYS.reduce((sum, key) => sum + totals[key], 0);
  return {
    entries: enhancedEntries,
    totals: {
      federalWotc: totals[TAX_CREDIT_FILTERS.FEDERAL_WOTC],
      federalNonWotc: totals[TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC],
      state: totals[TAX_CREDIT_FILTERS.STATE],
      total: totalValue,
    },
    projectedCredit: totalValue,
    hasProjectionValue:
      normalizedProjected !== null ||
      enhancedEntries.some((entry) => entry.amount > 0),
    categoryBreakdown,
  };
};

const sortBusinessesByExactCompanyName = (
  items,
  searchBusinessName,
  streetAddress,
  city,
  state,
  zipCode
) => {
  const searchName = normalizeName(searchBusinessName || "");
  const searchAddress = normalizeName(streetAddress || "");
  const searchCity = normalizeName(city || "");
  const searchState = normalizeName(state || "");
  const searchZip = (zipCode || "").toString().trim().toLowerCase();

  if (!searchName && !searchAddress && !searchCity && !searchState && !searchZip) {
    return items;
  }

  return [...items]
    .map((item, index) => {
      const itemName = normalizeName(item.businessName || "");
      const itemAddress = normalizeName(item.physicalAddress || "");
      const itemCity = normalizeName(item.physicalCity || "");
      const itemState = normalizeName(item.state || "");
      const itemZip = (item.physicalZip || "").toString().trim().toLowerCase();

      const isNameExact = !!searchName && itemName === searchName;
      const isAddressExact = !!searchAddress && itemAddress === searchAddress;
      const isCityExact = !!searchCity && itemCity === searchCity;
      const isStateExact = !!searchState && itemState === searchState;
      const isZipExact = !!searchZip && itemZip === searchZip;

      let score = 0;
      if (isNameExact) score += 2;
      if (isAddressExact) score += 2;
      if (isCityExact) score += 1;
      if (isStateExact) score += 1;
      if (isZipExact) score += 1;

      return { item, score, index };
    })
    .sort((a, b) => {
      const hasSearchName = !!searchName;
      const aNameExact =
        hasSearchName &&
        normalizeName(a.item.businessName || "") === searchName;
      const bNameExact =
        hasSearchName &&
        normalizeName(b.item.businessName || "") === searchName;

      if (aNameExact && !bNameExact) return -1;
      if (!aNameExact && bNameExact) return 1;

      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((entry) => entry.item);
};

const mergeBusinessesByBusinessId = (previous, next) => {
  const prev = Array.isArray(previous) ? previous : [];
  const nx = Array.isArray(next) ? next : [];
  const combined = [...prev, ...nx];
  const byId = new Map();
  combined.forEach((item, index) => {
    const key = item?.businessId || item?.prospectBusinessId || `row-${index}`;
    if (!byId.has(key)) {
      byId.set(key, item);
    }
  });
  return Array.from(byId.values());
};

const getTaxCreditDescriptions = (taxCreditsArr) => {
  if (!Array.isArray(taxCreditsArr) || taxCreditsArr.length === 0) return [];
  return (taxCreditsArr[0].availableTaxCredits || []).map((c) => {
    const baseName = c?.name || "";
    const category = c?.category || c?.Category;
    const label = category ? `${baseName} (${category})` : baseName;
    return [label || "Unnamed credit", c?.description]
      .filter(Boolean)
      .join(": ");
  });
};

const formatAddress = (biz) =>
  [biz.physicalAddress, biz.physicalCity, biz.state, biz.physicalZip]
    .filter(Boolean)
    .join(", ");

const resolveBusinessIdentifier = (business) => {
  if (!business) return null;
  return (
    business.prospectBusinessId ??
    business.businessId ??
    business.id ??
    null
  );
};

const waitForNextFrame = () => {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  if (typeof window.requestAnimationFrame === "function") {
    return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
  }
  return Promise.resolve();
};

const createPrintableNode = (element) => {
  if (!element || typeof document === "undefined") return null;
  const clone = element.cloneNode(true);
  clone.id = `${element.id || "pdf-content"}-clone-${Date.now()}`;
  clone.style.position = "absolute";
  clone.style.top = "-9999px";
  clone.style.left = "-9999px";
  clone.style.width = `${element.scrollWidth || element.offsetWidth || 1024}px`;
  clone.style.maxWidth = "1200px";
  clone.style.background = "#ffffff";
  clone.style.zIndex = "0";
  clone.style.opacity = "1";
  clone.style.pointerEvents = "none";
  document.body.appendChild(clone);
  return clone;
};

const getZoneCardStyle = (zoneCategory, isSelected = false) => {
  if (zoneCategory === EMPOWERMENT_ZONE_TYPES.EMPOWERMENT) {
    return {
      backgroundColor: "#e8f7ef",
      ...(isSelected ? {} : { borderColor: "#a6e1c2" }),
    };
  }
  if (zoneCategory === EMPOWERMENT_ZONE_TYPES.RURAL) {
    return {
      backgroundColor: "#fdf3cf",
      ...(isSelected ? {} : { borderColor: "#f0d27a" }),
    };
  }
  return {};
};

const areSelectionsEqual = (previous = [], next = []) => {
  if (previous === next) return true;
  if (!Array.isArray(previous) || !Array.isArray(next)) return false;
  if (previous.length !== next.length) return false;
  const prevIds = new Set(
    previous
      .map((entry) => resolveBusinessIdentifier(entry))
      .filter(Boolean)
  );
  if (prevIds.size !== previous.length) return false;
  for (const entry of next) {
    const nextId = resolveBusinessIdentifier(entry);
    if (!nextId || !prevIds.has(nextId)) {
      return false;
    }
  }
  return true;
};

const MANUAL_SEARCH_TEMPLATE_HEADERS = [
  "company name",
  "annual number of hires",
  "street address",
  "city",
  "state",
  "zip code",
  "businesstype",
];

const HEADER_VARIANTS = {
  "company name": ["company name", "company", "business name"],
  "annual number of hires": [
    "annual number of hires",
    "annual number",
    "annual hires",
    "annual hires count",
  ],
  "street address": ["street address", "address", "street"],
  city: ["city"],
  state: ["state"],
  "zip code": ["zip code", "zip", "zipcode", "postal code"],
  businesstype: ["businesstype", "business type", "type"],
};

const PRECALCULATED_REQUIRED_HEADERS = [
  "name",
  "address",
  "projected credit",
  "credits identified",
];

const PRECALCULATED_HEADER_ALIASES_BASE = {
  name: ["name", "business name"],
  prospectBusinessId: ["prospect business id", "business id", "prospect id"],
  website: ["website", "url"],
  address: ["address", "location"],
  latitude: ["latitude", "lat"],
  longitude: ["longitude", "lng", "long"],
  empowermentZone: ["empowerment zone"],
  projectedCredit: ["projected credit", "projected tax credit"],
  federalWotc: ["federal wotc", "federal wotc credits", "federal wotc credit"],
  federalNonWotc: [
    "federal non wotc",
    "federal non-wotc",
    "federal (non-wotc)",
    "federal non wotc credits",
    "federal non-wotc credits",
    "federal (non-wotc) credits",
  ],
  stateCredits: ["state credits", "state credit"],
  employees: ["employees", "employee count"],
  annualHires: ["annual hires", "annual number of hires"],
  censusTract: ["census tract"],
  fipsCode: ["fips code"],
  stateFips: ["state fips"],
  countyFips: ["county fips"],
  businessType: ["business type", "category"],
  naicsCode: ["naics code"],
  naicsDescription: ["naics description"],
  creditsIdentified: ["credits identified", "available credits"],
  phone: ["phone"],
  email: ["email"],
  salesVolume: ["sales volume", "annual sales"],
};

const CATEGORY_HEADER_ALIAS_MAP = CATEGORY_EXPORT_COLUMNS.reduce(
  (acc, column) => {
    const aliasSet = new Set([
      column.label,
      column.key,
      ...(column.aliases || []),
      `${column.label} credit`,
      `${column.label} projected credit`,
    ]);
    acc[column.key] = Array.from(aliasSet);
    return acc;
  },
  {}
);

const PRECALCULATED_HEADER_ALIASES = {
  ...PRECALCULATED_HEADER_ALIASES_BASE,
  ...CATEGORY_HEADER_ALIAS_MAP,
};

const UPLOAD_MODES = {
  TEMPLATE: "template",
  PRECALCULATED: "precalculated",
};

const UPLOAD_MODE_OPTIONS = [
  {
    value: UPLOAD_MODES.TEMPLATE,
    title: "Use Company Information Template",
  },
  {
    value: UPLOAD_MODES.PRECALCULATED,
    title: "Use Pre-Calculated Excel",
  },
];

const buildHeaderLookup = (headerRow) => {
  const lookup = {};
  if (!headerRow) return lookup;
  headerRow.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
    const normalized = normalizeHeaderText(cell.value);
    if (normalized && !lookup[normalized]) {
      lookup[normalized] = columnNumber;
    }
  });
  return lookup;
};

const resolveHeaderColumnIndex = (lookup, key) => {
  if (!lookup) return null;
  const normalizedKey = normalizeHeaderText(key);
  if (normalizedKey && lookup[normalizedKey]) {
    return lookup[normalizedKey];
  }
  const variants = PRECALCULATED_HEADER_ALIASES[key] || [];
  for (const variant of variants) {
    const normalizedVariant = normalizeHeaderText(variant);
    if (normalizedVariant && lookup[normalizedVariant]) {
      return lookup[normalizedVariant];
    }
  }
  return null;
};

const hasValidPrecalculatedHeaders = (lookup) =>
  PRECALCULATED_REQUIRED_HEADERS.every((header) =>
    resolveHeaderColumnIndex(lookup, header)
  );

const resolvePrecalculatedHeaderLookup = (worksheet) => {
  if (!worksheet) {
    return { headerLookup: {}, headerRowNumber: null };
  }
  const maxScanDepth = Math.min(
    worksheet.actualRowCount || worksheet.rowCount || 1,
    10
  );
  for (let rowNumber = 1; rowNumber <= maxScanDepth; rowNumber++) {
    const row = worksheet.getRow(rowNumber);
    const lookup = buildHeaderLookup(row);
    if (hasValidPrecalculatedHeaders(lookup)) {
      return { headerLookup: lookup, headerRowNumber: rowNumber };
    }
  }
  const fallbackRow = worksheet.getRow(1);
  return {
    headerLookup: buildHeaderLookup(fallbackRow),
    headerRowNumber: 1,
  };
};

const getRowCell = (row, lookup, key) => {
  const columnIndex = resolveHeaderColumnIndex(lookup, key);
  if (!columnIndex) return null;
  return row.getCell(columnIndex);
};

const getCellTextFromRow = (row, lookup, key) => {
  const cell = getRowCell(row, lookup, key);
  return cell ? getCellStringValue(cell.value) : "";
};

const parseEligibleCreditsFromValue = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((entry) =>
        typeof entry === "string" ? normalizeCreditLabel(entry) : normalizeCreditLabel(entry?.name)
      )
      .filter(Boolean);
  }
  return String(value)
    .split(/[\n|]+/)
    .map((entry) => normalizeCreditLabel(entry))
    .filter(Boolean);
};

const extractPrecalculatedCategoryTotals = (row, lookup) => {
  const totals = initializeCategoryTotals();
  let hasPositiveValue = false;
  CATEGORY_EXPORT_COLUMNS.forEach((column) => {
    const rawValue = getCellTextFromRow(row, lookup, column.key);
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return;
    }
    const parsed = parseCreditAmountValue(rawValue);
    if (parsed !== null) {
      totals[column.key] = parsed;
      if (!hasPositiveValue && typeof parsed === "number" && parsed > 0) {
        hasPositiveValue = true;
      }
    }
  });
  return { totals, hasPositiveValue };
};


const sanitizeWebsiteValue = (value) => {
  if (typeof value === "string") {
    return value.trim().replace(/^(https?:\/\/)/i, "");
  }
  if (value === undefined || value === null) {
    return "";
  }
  return String(value).trim().replace(/^(https?:\/\/)/i, "");
};

const parseAddressPartsFromText = (text = "") => {
  if (typeof text !== "string") {
    return { streetAddress: "", city: "", state: "", zip: "" };
  }
  const segments = text
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean);
  const [streetAddress = "", city = "", stateSegment = "", zipSegment = ""] =
    segments;
  let state = stateSegment;
  let zip = zipSegment;
  if (!zip && state && /\d/.test(state)) {
    const match = state.match(/(\d{5}(?:-\d{4})?)$/);
    if (match) {
      zip = match[1];
      state = state.replace(match[1], "").trim();
    }
  }
  return {
    streetAddress,
    city,
    state,
    zip,
  };
};

const extractBusinessIdFromLink = (link) => {
  if (!link || typeof link !== "string") return null;
  const match = link.match(/credit-details\/([^/?]+)/i);
  if (!match || !match[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch (err) {
    return match[1];
  }
};

const resolveBusinessIdFromCell = (cell) => {
  if (!cell) return null;
  const hyperlink =
    (typeof cell.hyperlink === "string" && cell.hyperlink) ||
    (cell.value &&
      typeof cell.value === "object" &&
      typeof cell.value.hyperlink === "string" &&
      cell.value.hyperlink) ||
    null;
  return extractBusinessIdFromLink(hyperlink);
};

const sanitizePrecalculatedTextValue = (value) => {
  if (value === undefined || value === null) return "";
  const normalized = String(value).trim();
  if (!normalized) return "";
  if (normalized.toLowerCase() === "no data available") {
    return "";
  }
  return normalized;
};

const buildBusinessesFromPrecalculatedWorksheet = (worksheet) => {
  if (!worksheet) {
    return { businesses: [], hasExplicitProspectIds: false };
  }
  const { headerLookup, headerRowNumber } = resolvePrecalculatedHeaderLookup(
    worksheet
  );
  if (!hasValidPrecalculatedHeaders(headerLookup)) {
    throw new Error(
      "Please upload the Excel file that was downloaded from Generate a Credit Map."
    );
  }
  const businesses = [];
  let explicitProspectIds = 0;
  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (headerRowNumber && rowNumber <= headerRowNumber) return;
    const nameCell = getRowCell(row, headerLookup, "name");
    const businessName = getCellStringValue(nameCell?.value).trim();
    const addressText = getCellTextFromRow(row, headerLookup, "address");
    const projectedText = getCellTextFromRow(row, headerLookup, "projectedCredit");
    const creditsText = getCellTextFromRow(row, headerLookup, "creditsIdentified");
    const rowHasValues =
      businessName ||
      addressText ||
      projectedText ||
      creditsText ||
      row.values.some(
        (value) =>
          value !== null &&
          value !== undefined &&
          String(value).trim() !== ""
      );
    if (!rowHasValues) {
      return;
    }
    const employeesValue = parseCreditAmountValue(
      getCellTextFromRow(row, headerLookup, "employees")
    );
    const annualHiresValue = parseCreditAmountValue(
      getCellTextFromRow(row, headerLookup, "annualHires")
    );
    const projectedCreditValue = parseCreditAmountValue(projectedText);
    const federalWotcValue = parseCreditAmountValue(
      getCellTextFromRow(row, headerLookup, "federalWotc")
    );
    const federalNonWotcValue = parseCreditAmountValue(
      getCellTextFromRow(row, headerLookup, "federalNonWotc")
    );
    const stateCreditsValue = parseCreditAmountValue(
      getCellTextFromRow(row, headerLookup, "stateCredits")
    );
    const creditsList = parseEligibleCreditsFromValue(creditsText);
    const latValue = parseFloat(getCellTextFromRow(row, headerLookup, "latitude"));
    const lngValue = parseFloat(getCellTextFromRow(row, headerLookup, "longitude"));
    const salesVolumeValue = parseCreditAmountValue(
      getCellTextFromRow(row, headerLookup, "salesVolume")
    );
    const addressParts = parseAddressPartsFromText(addressText);
    const { totals: rowCategoryTotals, hasPositiveValue: hasCategoryTotals } =
      extractPrecalculatedCategoryTotals(row, headerLookup);
    const precalculatedCreditTotals = (() => {
      const totals = {
        federalWotc:
          typeof federalWotcValue === "number" ? federalWotcValue : null,
        federalNonWotc:
          typeof federalNonWotcValue === "number" ? federalNonWotcValue : null,
        state:
          typeof stateCreditsValue === "number" ? stateCreditsValue : null,
      };
      const hasAnyValue = Object.values(totals).some(
        (value) => typeof value === "number"
      );
      return hasAnyValue ? totals : undefined;
    })();

    const explicitProspectId = sanitizePrecalculatedTextValue(
      getCellTextFromRow(row, headerLookup, "prospectBusinessId")
    );
    if (explicitProspectId) {
      explicitProspectIds += 1;
    }
    const identifier =
      explicitProspectId ||
      resolveBusinessIdFromCell(nameCell) ||
      `precalculated-${rowNumber}`;
    const stateFipsValue = sanitizePrecalculatedTextValue(
      getCellTextFromRow(row, headerLookup, "stateFips")
    );
    const countyFipsValue = sanitizePrecalculatedTextValue(
      getCellTextFromRow(row, headerLookup, "countyFips")
    );
    const businessTypeValue = sanitizePrecalculatedTextValue(
      getCellTextFromRow(row, headerLookup, "businessType")
    );

    businesses.push({
      businessName: businessName || "Unknown business",
      prospectBusinessId: identifier,
      businessId: identifier,
      lat: Number.isFinite(latValue) ? latValue : undefined,
      lng: Number.isFinite(lngValue) ? lngValue : undefined,
      latitude: Number.isFinite(latValue) ? latValue : undefined,
      longitude: Number.isFinite(lngValue) ? lngValue : undefined,
      empowermentZone:
        getCellTextFromRow(row, headerLookup, "empowermentZone") || "",
      censusTract: getCellTextFromRow(row, headerLookup, "censusTract") || "",
      fipsCode: getCellTextFromRow(row, headerLookup, "fipsCode") || "",
      stateFips: stateFipsValue || undefined,
      stateFIPS: stateFipsValue || undefined,
      countyFips: countyFipsValue || undefined,
      countyFIPS: countyFipsValue || undefined,
      naics: getCellTextFromRow(row, headerLookup, "naicsCode") || "",
      naicS01: getCellTextFromRow(row, headerLookup, "naicsCode") || "",
      naicS01Description:
        getCellTextFromRow(row, headerLookup, "naicsDescription") || "",
      naicsDescription:
        getCellTextFromRow(row, headerLookup, "naicsDescription") || "",
      businessType: businessTypeValue || undefined,
      physicalAddress: addressParts.streetAddress,
      physicalCity: addressParts.city,
      state: addressParts.state,
      physicalZip: addressParts.zip,
      locationEmployeesCount:
        typeof employeesValue === "number" ? employeesValue : undefined,
      employees:
        typeof employeesValue === "number" ? employeesValue : undefined,
      annualHires:
        typeof annualHiresValue === "number" ? annualHiresValue : undefined,
      annualHiresOverride:
        typeof annualHiresValue === "number" ? annualHiresValue : undefined,
      totalProjectedTaxCreditAmount:
        typeof projectedCreditValue === "number"
          ? projectedCreditValue
          : undefined,
      projectedCredit:
        typeof projectedCreditValue === "number"
          ? projectedCreditValue
          : undefined,
      taxCredits: [
        {
          totalProjectedTaxCreditAmount:
            typeof projectedCreditValue === "number"
              ? projectedCreditValue
              : null,
          availableTaxCredits: creditsList.map((label) => ({ name: label })),
        },
      ],
      eligibleCredits: creditsList,
      precalculatedCategoryTotals: hasCategoryTotals ? rowCategoryTotals : undefined,
      precalculatedCreditTotals,
      url: sanitizeWebsiteValue(
        getCellTextFromRow(row, headerLookup, "website")
      ),
      website: getCellTextFromRow(row, headerLookup, "website"),
      phone: getCellTextFromRow(row, headerLookup, "phone"),
      email: getCellTextFromRow(row, headerLookup, "email"),
      salesVolume:
        typeof salesVolumeValue === "number" ? salesVolumeValue : undefined,
      isSelected: false,
      precalculatedHasProspectId: Boolean(explicitProspectId),
    });
  });
  return {
    businesses,
    hasExplicitProspectIds:
      businesses.length > 0 && explicitProspectIds === businesses.length,
  };
};

const hyperlinkFormulaRegex = /=HYPERLINK\("([^"]+)"[,;]"([^"]+)"\)/i;

const tryExtractHyperlinkLabel = (value) => {
  if (typeof value !== "string") return null;
  const match = value.match(hyperlinkFormulaRegex);
  if (!match) return null;
  const decode = (text) => text.replace(/""/g, '"');
  return decode(match[2]);
};

const getCellStringValue = (value) => {
  if (typeof value === "string") {
    return tryExtractHyperlinkLabel(value) ?? value;
  }
  if (value && typeof value === "object") {
    if (typeof value.text === "string") return value.text;
    if (Array.isArray(value.richText)) {
      return value.richText.map((segment) => segment?.text || "").join("");
    }
    if (typeof value.result === "string") return value.result;
  }
  const fallback =
    value !== undefined && value !== null ? String(value) : "";
  if (typeof fallback === "string") {
    return tryExtractHyperlinkLabel(fallback) ?? fallback;
  }
  return fallback;
};

const normalizeHeaderText = (value) =>
  (getCellStringValue(value) || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const doesHeaderMatch = (actualValue, expectedKey) => {
  const normalizedActual = normalizeHeaderText(actualValue);
  if (!normalizedActual) return false;
  const candidates = HEADER_VARIANTS[expectedKey] || [expectedKey];
  return candidates.some((candidate) => {
    const normalizedTarget = normalizeHeaderText(candidate);
    if (!normalizedTarget) return false;
    return (
      normalizedActual === normalizedTarget ||
      normalizedActual.includes(normalizedTarget) ||
      normalizedTarget.includes(normalizedActual)
    );
  });
};

const VALID_FILE_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroenabled.12",
  "application/vnd.ms-excel.sheet.binary.macroenabled.12",
  "application/csv",
  "text/csv",
  "application/excel",
  "text/plain",
];

const VALID_FILE_EXTENSIONS = ["xlsx", "xls", "xlsm", "xlsb", "csv"];

const isCsvFile = (file) => {
  if (!file) return false;
  const lowerName = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  return lowerName.endsWith(".csv") || type.includes("csv");
};

const isValidUploadFile = (file) => {
  if (!file) return false;
  const normalizedType = (file.type || "").toLowerCase();
  const extension = ((file.name || "").split(".").pop() || "").toLowerCase().trim();
  if (extension && VALID_FILE_EXTENSIONS.includes(extension)) {
    return true;
  }
  if (!normalizedType && extension === "csv") {
    return true;
  }
  return (
    VALID_FILE_TYPES.includes(normalizedType) ||
    normalizedType.includes("excel") ||
    normalizedType.includes("spreadsheetml") ||
    normalizedType.includes("csv")
  );
};

const parseCsvContent = (text) => {
  if (typeof text !== "string" || !text.trim()) return [];
  const rows = [];
  let currentValue = "";
  let currentRow = [];
  let inQuotes = false;

  const pushValue = () => {
    currentRow.push(currentValue);
    currentValue = "";
  };

  const pushRow = () => {
    if (currentRow.length || currentValue) {
      pushValue();
      rows.push(currentRow);
      currentRow = [];
    }
  };

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentValue += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      pushValue();
    } else if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") {
        i++;
      }
      pushRow();
    } else {
      currentValue += char;
    }
  }

  if (currentValue || currentRow.length) {
    pushValue();
    rows.push(currentRow);
  }

  return rows.filter((row) => row.some((value) => value && value.trim() !== ""));
};

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || "");
    reader.onerror = (err) => reject(err);
    reader.readAsText(file);
  });

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });

const loadWorksheetFromFile = async (file) => {
  if (!file) return null;
  const workbook = new ExcelJS.Workbook();
  if (isCsvFile(file)) {
    const csvString =
      typeof file.text === "function" ? await file.text() : await readFileAsText(file);
    const worksheet = workbook.addWorksheet("Sheet1");
    parseCsvContent(csvString).forEach((row) => worksheet.addRow(row));
    return worksheet;
  }
  const buffer =
    typeof file.arrayBuffer === "function"
      ? await file.arrayBuffer()
      : await readFileAsArrayBuffer(file);
  await workbook.xlsx.load(buffer);
  return workbook.getWorksheet(1) || workbook.worksheets?.[0] || null;
};

const trimValue = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const getErrorType = (errorMessage = "") => {
  const m = errorMessage.toLowerCase();
  if (m.includes("city")) return "Missing city";
  if (m.includes("state") && m.includes("2-letter")) return "Invalid state";
  if (m.includes("zip") || m.includes("postal")) return "Invalid zip";
  if (m.includes("no matching") || m.includes("no census")) return "Address not found";
  if (m.includes("address")) return "Address error";
  return "API error";
};

const getErrorHint = (errorMessage = "") => {
  const m = errorMessage.toLowerCase();
  if (m.includes("no matching"))
    return "Could not find a matching business — check for a typo in the street name or try a different zip code.";
  if (m.includes("no census") || m.includes("census tract"))
    return "Address validated but no census tract found — check the zip code.";
  if (m.includes("geocode"))
    return "Could not geocode — check for a typo in the street name.";
  if (m.includes("city"))
    return "City is missing — please enter the city name.";
  if (m.includes("state"))
    return "State must be a valid 2-letter abbreviation (e.g. CA, TX).";
  if (m.includes("zip") || m.includes("postal"))
    return "Zip code is invalid — enter a 5-digit (12345) or 9-digit (12345-6789) zip code.";
  return errorMessage;
};

const getErrorHighlightField = (errorMessage = "") => {
  const m = errorMessage.toLowerCase();
  if (m.includes("city")) return "city";
  if (m.includes("state")) return "state";
  if (m.includes("zip") || m.includes("postal")) return "zipCode";
  return "streetAddress";
};

const normalizeZipDigits = (value) => {
  const normalized = trimValue(value);
  if (!normalized) return "";
  const digitsOnly = normalized.replace(/\D/g, "");
  if (!digitsOnly) return "";
  if (digitsOnly.length < 5) {
    return digitsOnly.padStart(5, "0");
  }
  if (digitsOnly.length >= 9) {
    return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5, 9)}`;
  }
  return digitsOnly.slice(0, 5);
};

const extractBusinessTypeValue = (input) => {
  const raw = trimValue(input);
  if (!raw) return null;
  const tokens = raw
    .split(/[;|,]/)
    .map((token) => token.trim())
    .filter(Boolean);
  const descriptionToken = tokens.find((token) => /\D/.test(token));
  if (descriptionToken) return descriptionToken;
  const codeToken = tokens.find((token) => /^\d{6}$/.test(token.replace(/\D/g, "")));
  if (codeToken) return codeToken.replace(/\D/g, "").slice(0, 6);
  return raw;
};

const extractBusinessTypeMetadata = (input) => {
  const raw = trimValue(input);
  if (!raw) return { businessType: null, naicsCode: null };
  const tokens = raw
    .split(/[;|,]/)
    .map((token) => token.trim())
    .filter(Boolean);
  const descriptionToken = tokens.find((token) => /\D/.test(token));
  const codeToken = tokens
    .map((token) => token.replace(/\D/g, ""))
    .find((token) => token.length === 6);
  return {
    businessType: descriptionToken || (codeToken ? codeToken.slice(0, 6) : raw),
    naicsCode: codeToken ? codeToken.slice(0, 6) : null,
  };
};

const validateRowData = ({
  companyName,
  annualHiresOverride,
  streetAddress,
  city,
  state,
  zipCode,
}) => {
  const name = trimValue(companyName);
  if (!name) {
    return "Company name is required.";
  }

  const annual = trimValue(annualHiresOverride);
  if (annual && Number.isNaN(Number(annual))) {
    return "Annual number of hires must be a valid number.";
  }

  const normalizedState = trimValue(state);
  if (normalizedState && normalizedState.length !== 2) {
    return "State must be the 2-letter abbreviation.";
  }

  const normalizedZip = trimValue(zipCode);
  if (normalizedZip) {
    const digitsOnly = normalizedZip.replace(/\D/g, "");
    const effectiveZip = digitsOnly.length > 0 && digitsOnly.length < 5
      ? digitsOnly.padStart(5, "0")
      : normalizedZip;
    if (!/^\d{5}(-\d{4})?$/.test(effectiveZip)) {
      return "Zip code must be 5 digits (12345) or 9 digits (12345-6789).";
    }
  }

  const address = trimValue(streetAddress);
  if (!address) {
    return "Street address is required.";
  }

  const normalizedCity = trimValue(city);
  if (!normalizedCity) {
    return "City is required.";
  }

  return null;
};

const MAX_CONCURRENT_LOOKUPS = 300;
const INITIAL_VISIBLE_COUNT = 30;
const PREFETCH_CHUNK_SIZE = 50;
const LOAD_MORE_INCREMENT = 50;
const INITIAL_LOADED_LIMIT = INITIAL_VISIBLE_COUNT + PREFETCH_CHUNK_SIZE;
const GRAPH_CARD_MIN_WIDTH = 320;
const MAX_GRAPH_CARDS_PER_SLIDE = 4;
const TOTAL_GRAPH_CARDS = 4; // Update alongside graphCardBuilders if cards are added or removed.

const CreditMapGenerator = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const dispatch = useDispatch();
  const appUserName =
    useSelector((state) => state.App.userName) || "John Smith";
  const resultsCacheEntry = useSelector((state) => state.resultsCache?.creditMap);
  const cachedCreditMapData = resultsCacheEntry?.data || null;
  const cachedImportSessionId = resultsCacheEntry?.importSessionId || null;
  const shouldHydrateFromCache =
    Boolean(
      cachedCreditMapData &&
        Array.isArray(cachedCreditMapData.businesses) &&
        cachedCreditMapData.businesses.length
    );
  const [activeImportSessionId, setActiveImportSessionId] = useState(
    cachedImportSessionId || null
  );
  const [uploadStatus, setUploadStatus] = useState(
    shouldHydrateFromCache ? Boolean(cachedCreditMapData?.uploadStatus) : false
  );
  const [businesses, setBusinesses] = useState(
    shouldHydrateFromCache ? cachedCreditMapData?.businesses || [] : []
  );
  const [activeTab, setActiveTab] = useState("findBusiness");
  const [processing, setProcessing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(
    shouldHydrateFromCache ? cachedCreditMapData?.uploadedFileName || "" : ""
  );
  const [showMap, setShowMap] = useState(
    shouldHydrateFromCache ? Boolean(cachedCreditMapData?.showMap) : false
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUpload, setShowUpload] = useState(
    shouldHydrateFromCache ? Boolean(cachedCreditMapData?.showUpload) : true
  );
  const cachedUploadMode = shouldHydrateFromCache
    ? cachedCreditMapData?.uploadMode
    : null;
  const resolvedInitialUploadMode = Object.values(UPLOAD_MODES).includes(
    cachedUploadMode
  )
    ? cachedUploadMode
    : UPLOAD_MODES.TEMPLATE;
  const [uploadMode, setUploadMode] = useState(resolvedInitialUploadMode);
  const [isPrecalculatedSession, setIsPrecalculatedSession] = useState(
    shouldHydrateFromCache
      ? Boolean(cachedCreditMapData?.isPrecalculatedSession)
      : false
  );
  const [precalculatedEditEnabled, setPrecalculatedEditEnabled] = useState(
    shouldHydrateFromCache
      ? Boolean(cachedCreditMapData?.precalculatedEditEnabled)
      : false
  );
  const annualHiresSaveButtonSx = {
    textTransform: "none",
    borderRadius: "6px",
    backgroundColor: "#FFCC33",
    color: "#000",
    borderColor: "#FFCC33",
    minWidth: 80,
    height: 38,
    px: 2,
    fontSize: "0.9rem",
    "&:hover": {
      backgroundColor: "#e6b800",
      borderColor: "#e6b800",
    },
    "&:active": {
      boxShadow: "none",
    },
  };
  const [isLoading, setIsLoading] = useState(false);
  const [failedRows, setFailedRows] = useState([]);
  const [failedRowEdits, setFailedRowEdits] = useState({});
  const [failedRowRetrying, setFailedRowRetrying] = useState({});
  const [failedRowEditingId, setFailedRowEditingId] = useState(null);
  const failedSectionRef = useRef(null);
  const [selectedTaxcredits, setSelectedTaxcredits] = useState(
    shouldHydrateFromCache ? cachedCreditMapData?.selectedTaxcredits || [] : []
  );
  const [isSelectionTrayCollapsed, setSelectionTrayCollapsed] = useState(
    shouldHydrateFromCache
      ? Boolean(cachedCreditMapData?.isSelectionTrayCollapsed)
      : false
  );
  const allowedZoneFilterValues = [
    ...Object.values(EMPOWERMENT_FILTER_VALUES),
    EMPOWERMENT_ZONE_TYPES.NONE,
  ];
  const cachedZoneFilterValue =
    (shouldHydrateFromCache &&
      (cachedCreditMapData?.empowermentFilter ||
        cachedCreditMapData?.zoneFilter)) ||
    null;
  const initialEmpowermentFilterValue = allowedZoneFilterValues.includes(
    cachedZoneFilterValue
  )
    ? cachedZoneFilterValue
    : EMPOWERMENT_FILTER_VALUES.ALL;
  const [empowermentFilter, setEmpowermentFilter] = useState(
    initialEmpowermentFilterValue
  );
  const [naicsFilter, setNaicsFilter] = useState(
    shouldHydrateFromCache ? cachedCreditMapData?.naicsFilter || "" : ""
  );
  const resolveInitialTaxCreditFilter = () => {
    if (!shouldHydrateFromCache) {
      return TAX_CREDIT_FILTERS.ALL;
    }
    const cachedValue = cachedCreditMapData?.taxCreditFilter;
    if (
      cachedValue &&
      (Object.values(TAX_CREDIT_FILTERS).includes(cachedValue) ||
        cachedValue === "federal")
    ) {
      return cachedValue === "federal"
        ? TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC
        : cachedValue;
    }
    const cachedScope = cachedCreditMapData?.creditTotalsScope;
    if (
      cachedScope &&
      (CREDIT_SCOPE_VALUE_SET.has(cachedScope) || cachedScope === "federal")
    ) {
      return resolveFilterFromScope(cachedScope);
    }
    return TAX_CREDIT_FILTERS.ALL;
  };
  const [taxCreditFilter, setTaxCreditFilter] = useState(
    resolveInitialTaxCreditFilter
  );
  const [creditCategoryFilter, setCreditCategoryFilter] = useState(
    shouldHydrateFromCache
      ? cachedCreditMapData?.creditCategoryFilter || []
      : []
  );
  const [isFilterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);
  const [isResultsReady, setIsResultsReady] = useState(
    shouldHydrateFromCache ? Boolean(cachedCreditMapData?.isResultsReady) : false
  );
  const [displayedResultsCount, setDisplayedResultsCount] = useState(
    shouldHydrateFromCache ? cachedCreditMapData?.displayedResultsCount || 0 : 0
  );
  const [viewMode, setViewMode] = useState("map");
  const [graphSlideIndex, setGraphSlideIndex] = useState(0);
  const [cardsPerSlide, setCardsPerSlide] = useState(2);
  const graphViewRef = useRef(null);

  const measureGraphCardsPerSlide = useCallback(() => {
    if (typeof window === "undefined") return;
    const containerWidth =
      graphViewRef.current?.clientWidth ||
      graphViewRef.current?.offsetWidth ||
      window.innerWidth;
    if (!containerWidth) return;
    const nextValue = Math.max(
      1,
      Math.min(
        MAX_GRAPH_CARDS_PER_SLIDE,
        Math.floor(containerWidth / GRAPH_CARD_MIN_WIDTH) || 1
      )
    );
    setCardsPerSlide((prev) => (prev === nextValue ? prev : nextValue));
  }, []);

  useEffect(() => {
    if (viewMode !== "graph") return undefined;
    measureGraphCardsPerSlide();
    if (typeof window === "undefined") return undefined;
    const handleResize = () => measureGraphCardsPerSlide();
    window.addEventListener("resize", handleResize);
    let observer;
    if (typeof ResizeObserver !== "undefined" && graphViewRef.current) {
      observer = new ResizeObserver(handleResize);
      observer.observe(graphViewRef.current);
    }
    return () => {
      window.removeEventListener("resize", handleResize);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [measureGraphCardsPerSlide, viewMode]);

  useEffect(() => {
    setGraphSlideIndex((prev) => {
      const slidesPerView = Math.max(cardsPerSlide, 1);
      const computedMaxIndex = Math.max(
        Math.ceil(TOTAL_GRAPH_CARDS / slidesPerView) - 1,
        0
      );
      return prev > computedMaxIndex ? computedMaxIndex : prev;
    });
  }, [cardsPerSlide]);

  useEffect(() => {
    if (!isFilterMenuOpen) return undefined;
    if (typeof document === "undefined") return undefined;
    const handleClickOutside = (event) => {
      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setFilterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterMenuOpen]);

  const [taxCreditData, setTaxCreditData] = useState(
    shouldHydrateFromCache ? cachedCreditMapData?.taxCreditData || {} : {}
  );
  const [taxCreditLoading, setTaxCreditLoading] = useState({});
  const [annualHiresDrafts, setAnnualHiresDrafts] = useState(
    shouldHydrateFromCache ? cachedCreditMapData?.annualHiresDrafts || {} : {}
  );
  const [annualHiresSaving, setAnnualHiresSaving] = useState(
    shouldHydrateFromCache ? cachedCreditMapData?.annualHiresSaving || {} : {}
  );
  const [visibleCount, setVisibleCount] = useState(
    shouldHydrateFromCache
      ? cachedCreditMapData?.visibleCount || INITIAL_VISIBLE_COUNT
      : INITIAL_VISIBLE_COUNT
  );
  const [loadedLimit, setLoadedLimit] = useState(
    shouldHydrateFromCache
      ? cachedCreditMapData?.loadedLimit || INITIAL_LOADED_LIMIT
      : INITIAL_LOADED_LIMIT
  );
  const [hasQueuedRows, setHasQueuedRows] = useState(
    shouldHydrateFromCache ? Boolean(cachedCreditMapData?.hasQueuedRows) : false
  );
  const [isProcessingQueuedRows, setIsProcessingQueuedRows] = useState(
    shouldHydrateFromCache
      ? Boolean(cachedCreditMapData?.isProcessingQueuedRows)
      : false
  );
  const [isLoadingMoreRows, setIsLoadingMoreRows] = useState(false);
  const beginNewImportSession = useCallback(() => {
    const sessionId = Date.now();
    setActiveImportSessionId(sessionId);
    dispatch(clearCreditMapResults());
  }, [dispatch]);

  const pendingRowsRef = useRef([]);
  const processingRowsRef = useRef(false);
  const filteredBusinessesRef = useRef([]);
  const rowChunkPromiseRef = useRef(null);
  const resultsCacheHydratedRef = useRef(false);
  const cachePayloadRef = useRef(null);

  const naicsDirectoryRef = useRef({});
  const naicsPrefixDirectoryRef = useRef({});
  const naicsDirectoryPromiseRef = useRef(null);
  const hasLoadedNaicsDirectoryRef = useRef(false);
  const [naicsDirectoryVersion, setNaicsDirectoryVersion] = useState(0);

  const ensureNaicsDirectory = useCallback(async () => {
    if (hasLoadedNaicsDirectoryRef.current) {
      return naicsDirectoryRef.current;
    }
    if (!naicsDirectoryPromiseRef.current) {
      naicsDirectoryPromiseRef.current = axiosInstance
        .get("/businessSearch/GetNaicsCodes")
        .then((response) => {
          const rawData = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
          const directory = {};
          const prefixDirectory = {};
          rawData.forEach((item) => {
            const normalized = normalizeNaicsDigits(item?.naicsCode);
            if (!normalized) return;
            directory[normalized] = {
              code: normalized,
              description: item?.naicsDescription || "",
            };
            const prefix = normalized.slice(0, 2);
            if (prefix && !prefixDirectory[prefix]) {
              prefixDirectory[prefix] = item?.naicsDescription || "";
            }
          });
          naicsDirectoryRef.current = directory;
          naicsPrefixDirectoryRef.current = prefixDirectory;
          hasLoadedNaicsDirectoryRef.current = true;
          setNaicsDirectoryVersion((prev) => prev + 1);
          return directory;
        })
        .catch((error) => {
          console.error("Unable to load NAICS codes", error);
          hasLoadedNaicsDirectoryRef.current = true;
          return naicsDirectoryRef.current;
        })
        .finally(() => {
          naicsDirectoryPromiseRef.current = null;
        });
    }
    return naicsDirectoryPromiseRef.current;
  }, []);

  const resolveNaicsDetails = useCallback(
    async (code) => {
      const normalized = normalizeNaicsDigits(code);
      if (!normalized) return null;
      if (naicsDirectoryRef.current[normalized]) {
        return naicsDirectoryRef.current[normalized];
      }
      const directory = await ensureNaicsDirectory();
      return directory?.[normalized] || null;
    },
    [ensureNaicsDirectory]
  );

  const safeBusinesses = Array.isArray(businesses) ? businesses : [];
  useEffect(() => {
    ensureNaicsDirectory();
  }, [ensureNaicsDirectory]);
  useEffect(() => {
    if (!showMap) {
      setViewMode("map");
    }
  }, [showMap]);
  const availableCreditCategories = useMemo(() => {
    if (!safeBusinesses.length) return [];
    const counts = new Map();
    safeBusinesses.forEach((biz) => {
      const categories = collectBusinessCategoryKeys(biz, taxCreditData);
      categories.forEach(({ key, label }) => {
        if (!key) return;
        const current = counts.get(key);
        if (current) {
          current.count += 1;
        } else {
          counts.set(key, {
            key,
            label: label || "Uncategorized",
            count: 1,
          });
        }
      });
    });
    return Array.from(counts.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    });
  }, [safeBusinesses, taxCreditData]);
  useEffect(() => {
    if (viewMode !== "graph") {
      setGraphSlideIndex(0);
    }
  }, [viewMode]);
  const sanitizedNaicsFilter = naicsFilter.trim();
  const isNaicsFilterActive = NAICS_FILTER_PATTERN.test(sanitizedNaicsFilter);
  const isNaicsFilterPending =
    naicsFilter !== "" && !isNaicsFilterActive;
  const hasCategoryFilters =
    Array.isArray(creditCategoryFilter) && creditCategoryFilter.length > 0;
  const categoryFilterSet = useMemo(() => {
    if (!hasCategoryFilters) return null;
    return new Set(
      creditCategoryFilter
        .map((value) => buildCategoryKey(value))
        .filter(Boolean)
    );
  }, [creditCategoryFilter, hasCategoryFilters]);
  const applyCategoryFilterToEntries = useCallback(
    (entries = [], categoryBreakdown = null) => {
      if (!categoryFilterSet || !categoryFilterSet.size) {
        return { entries, usedCategoryFallback: false };
      }
      const filtered = entries.filter(
        (entry) => entry.categoryKey && categoryFilterSet.has(entry.categoryKey)
      );
      if (filtered.length || !categoryBreakdown) {
        return { entries: filtered, usedCategoryFallback: false };
      }
      const synthetic = [];
      categoryFilterSet.forEach((key) => {
        const amount = categoryBreakdown?.[key];
        if (typeof amount === "number" && amount > 0) {
          const meta = CATEGORY_META_LOOKUP[key];
          synthetic.push({
            label: meta?.label || key,
            type: (() => {
              if (meta?.key === "fez") {
                return TAX_CREDIT_FILTERS.FEDERAL_WOTC;
              }
              if (meta?.type === "state") {
                return TAX_CREDIT_FILTERS.STATE;
              }
              if (meta?.type === "federal") {
                const normalizedLabel = normalizeTextValue(meta?.label);
                if (normalizedLabel.includes("wotc")) {
                  return TAX_CREDIT_FILTERS.FEDERAL_WOTC;
                }
                return TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC;
              }
              return TAX_CREDIT_FILTERS.STATE;
            })(),
            amount,
            category: meta?.label || key,
            categoryKey: key,
            isSyntheticCategory: true,
          });
        }
      });
      return {
        entries: synthetic,
        usedCategoryFallback: synthetic.length > 0,
      };
    },
    [categoryFilterSet]
  );
  const activeCreditFilter = taxCreditFilter;
  const creditTotalsScope = resolveScopeFromFilter(taxCreditFilter);
  const isEmpowermentFilterAll =
    empowermentFilter === EMPOWERMENT_FILTER_VALUES.ALL;
  const hasAdditionalFilters =
    isNaicsFilterActive ||
    activeCreditFilter !== TAX_CREDIT_FILTERS.ALL ||
    hasCategoryFilters;
  const filteredBusinesses = useMemo(() => {
    if (!safeBusinesses.length) return [];
    let list = safeBusinesses;
    const creditDisplayCache = new Map();
    const resolveCreditDisplay = (biz) => {
      if (!biz) return null;
      const cacheKey =
        resolveBusinessIdentifier(biz) ||
        biz?.prospectBusinessId ||
        biz?.businessId;
      if (cacheKey && creditDisplayCache.has(cacheKey)) {
        return creditDisplayCache.get(cacheKey);
      }
      const data = buildCreditDisplayData(biz, taxCreditData);
      if (cacheKey) {
        creditDisplayCache.set(cacheKey, data);
      }
      return data;
    };
    if (empowermentFilter === EMPOWERMENT_ZONE_TYPES.NONE) {
      list = list.filter(
        (biz) => getEmpowermentZoneCategories(biz.empowermentZone).length === 0
      );
    } else if (empowermentFilter !== EMPOWERMENT_FILTER_VALUES.ALL) {
      list = list.filter((biz) =>
        getEmpowermentZoneCategories(biz.empowermentZone).includes(
          empowermentFilter
        )
      );
    }
    if (isNaicsFilterActive) {
      list = list.filter((biz) =>
        doesBusinessMatchCodes(
          biz,
          [sanitizedNaicsFilter],
          BUSINESS_NAICS_FIELDS,
          { allowPrefixMatch: true }
        )
      );
    }
    if (categoryFilterSet && categoryFilterSet.size) {
      list = list.filter((biz) => {
        const categories = collectBusinessCategoryKeys(biz, taxCreditData);
        if (!categories.length) return false;
        return categories.some(
          ({ key }) => key && categoryFilterSet.has(key)
        );
      });
    }
    if (activeCreditFilter !== TAX_CREDIT_FILTERS.ALL) {
      list = list.filter((biz) => {
        const creditData = resolveCreditDisplay(biz);
        if (!creditData) return false;
        if (activeCreditFilter === TAX_CREDIT_FILTERS.STATE) {
          const hasStateEntries = creditData.entries.some(
            (entry) => entry.type === TAX_CREDIT_FILTERS.STATE
          );
          const qualifiesFromProjection =
            creditData.entries.length === 0 && creditData.hasProjectionValue;
          return hasStateEntries || qualifiesFromProjection;
        }
        return creditData.entries.some(
          (entry) => entry.type === activeCreditFilter
        );
      });
    }
    return list;
  }, [
    activeCreditFilter,
    categoryFilterSet,
    empowermentFilter,
    isNaicsFilterActive,
    sanitizedNaicsFilter,
    safeBusinesses,
    taxCreditData,
  ]);
  const totalFilteredCount = filteredBusinesses.length;
  const limitedBusinesses = useMemo(() => {
    if (!filteredBusinesses.length) return [];
    if (isPrecalculatedSession) {
      return filteredBusinesses;
    }
    const limit = Math.min(loadedLimit, filteredBusinesses.length);
    return filteredBusinesses.slice(0, limit);
  }, [filteredBusinesses, loadedLimit, isPrecalculatedSession]);
  const visibleBusinesses = useMemo(() => {
    if (!limitedBusinesses.length) return [];
    if (isPrecalculatedSession) {
      return limitedBusinesses;
    }
    const limit = Math.min(visibleCount, limitedBusinesses.length);
    return limitedBusinesses.slice(0, limit);
  }, [limitedBusinesses, visibleCount, isPrecalculatedSession]);
  useEffect(() => {
    filteredBusinessesRef.current = filteredBusinesses;
  }, [filteredBusinesses]);
  useEffect(() => {
    if (!activeImportSessionId) return;
    if (!businesses.length && !isResultsReady) return;
    const payload = {
      businesses,
      uploadStatus,
      showMap,
      showUpload,
      uploadedFileName,
      selectedTaxcredits,
      isSelectionTrayCollapsed,
      empowermentFilter,
      zoneFilter: empowermentFilter,
      naicsFilter,
      taxCreditFilter,
      creditCategoryFilter,
      isResultsReady,
      displayedResultsCount,
      taxCreditData,
      annualHiresDrafts,
      annualHiresSaving,
      visibleCount,
      loadedLimit,
      hasQueuedRows,
      isProcessingQueuedRows,
       viewMode,
       uploadMode,
      isPrecalculatedSession,
      precalculatedEditEnabled,
      failedRows,
    };
    const serialized = JSON.stringify(payload);
    if (
      cachePayloadRef.current &&
      cachePayloadRef.current.sessionId === activeImportSessionId &&
      cachePayloadRef.current.serialized === serialized
    ) {
      return;
    }
    cachePayloadRef.current = {
      sessionId: activeImportSessionId,
      serialized,
    };
    dispatch(
      setCreditMapResults({
        importSessionId: activeImportSessionId,
        data: payload,
      })
    );
  }, [
    activeImportSessionId,
    businesses,
    uploadStatus,
    showMap,
    showUpload,
    uploadedFileName,
    selectedTaxcredits,
    isSelectionTrayCollapsed,
    empowermentFilter,
    naicsFilter,
    taxCreditFilter,
    isResultsReady,
    displayedResultsCount,
    taxCreditData,
    annualHiresDrafts,
    annualHiresSaving,
    visibleCount,
    loadedLimit,
    hasQueuedRows,
    isProcessingQueuedRows,
    viewMode,
    uploadMode,
    isPrecalculatedSession,
    precalculatedEditEnabled,
    failedRows,
    dispatch,
  ]);
  const allVisibleSelected =
    visibleBusinesses.length > 0 &&
    visibleBusinesses.every((biz) => biz.isSelected);
  const hasMoreResults = isPrecalculatedSession
    ? false
    : visibleBusinesses.length < filteredBusinesses.length ||
      hasQueuedRows ||
      isProcessingQueuedRows;
  const isPrecalculatedEditable =
    isPrecalculatedSession && precalculatedEditEnabled;
  const filtersLocked =
    hasQueuedRows || isProcessingQueuedRows || !isResultsReady;
  const hasUploadStarted =
    uploadStatus || processing || hasQueuedRows || isProcessingQueuedRows;
  const creditMapBusy = filtersLocked && hasUploadStarted;
  useEffect(() => {
    dispatch(setCreditMapBusy(creditMapBusy));
    return () => {
      dispatch(setCreditMapBusy(false));
    };
  }, [dispatch, creditMapBusy]);
  const baseCreditTotals = useMemo(
    () => computeCreditTypeTotals(visibleBusinesses, taxCreditData),
    [visibleBusinesses, taxCreditData]
  );
  const creditTotals = useMemo(() => {
    if (!categoryFilterSet || !categoryFilterSet.size) {
      return baseCreditTotals;
    }
    const totals = {
      federalWotc: 0,
      federalNonWotc: 0,
      state: 0,
      total: 0,
    };
    visibleBusinesses.forEach((biz) => {
      const creditData = buildCreditDisplayData(biz, taxCreditData);
      const { entries: filteredEntries } = applyCategoryFilterToEntries(
        creditData.entries,
        creditData.categoryBreakdown
      );
      filteredEntries.forEach((entry) => {
        if (!entry.categoryKey || !categoryFilterSet.has(entry.categoryKey)) {
          return;
        }
        if (typeof entry.amount !== "number" || entry.amount <= 0) {
          return;
        }
        const bucket =
          entry.type === TAX_CREDIT_FILTERS.FEDERAL_WOTC
            ? "federalWotc"
            : entry.type === TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC
            ? "federalNonWotc"
            : "state";
        totals[bucket] += entry.amount;
      });
    });
    totals.total = totals.federalWotc + totals.federalNonWotc + totals.state;
    return totals;
  }, [
    applyCategoryFilterToEntries,
    baseCreditTotals,
    categoryFilterSet,
    visibleBusinesses,
    taxCreditData,
  ]);
  const showFederalWotcChip =
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.ALL ||
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.FEDERAL_WOTC;
  const showFederalNonWotcChip =
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.ALL ||
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.FEDERAL_NON_WOTC;
  const showStateTotalChip =
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.ALL ||
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.STATE;
  const showGrandTotalChip =
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.ALL;
  const graphSummary = useMemo(() => {
    const summary = {
      totalBusinesses: visibleBusinesses.length,
      counts: {
        [TAX_CREDIT_FILTERS.FEDERAL_WOTC]: 0,
        [TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC]: 0,
        [TAX_CREDIT_FILTERS.STATE]: 0,
      },
      amounts: {
        federalWotc: creditTotals.federalWotc,
        federalNonWotc: creditTotals.federalNonWotc,
        state: creditTotals.state,
        total: creditTotals.total,
      },
    };
    visibleBusinesses.forEach((biz) => {
      const creditData = buildCreditDisplayData(biz, taxCreditData);
      const hasFederalWotc = creditData.entries.some(
        (entry) => entry.type === TAX_CREDIT_FILTERS.FEDERAL_WOTC
      );
      const hasFederalNonWotc = creditData.entries.some(
        (entry) => entry.type === TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC
      );
      const hasState =
        creditData.entries.some(
          (entry) => entry.type === TAX_CREDIT_FILTERS.STATE
        ) ||
        (!creditData.entries.length && creditData.projectedCredit !== null);
      if (hasFederalWotc) {
        summary.counts[TAX_CREDIT_FILTERS.FEDERAL_WOTC] += 1;
      }
      if (hasFederalNonWotc) {
        summary.counts[TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC] += 1;
      }
      if (hasState) {
        summary.counts[TAX_CREDIT_FILTERS.STATE] += 1;
      }
    });
    return summary;
  }, [visibleBusinesses, taxCreditData, creditTotals]);
  const zoneDistribution = useMemo(() => {
    const counts = {
      [EMPOWERMENT_ZONE_TYPES.EMPOWERMENT]: 0,
      [EMPOWERMENT_ZONE_TYPES.RURAL]: 0,
      [EMPOWERMENT_ZONE_TYPES.NONE]: 0,
    };
    visibleBusinesses.forEach((biz) => {
      const category = getEmpowermentZoneCategory(biz.empowermentZone);
      counts[category] = (counts[category] || 0) + 1;
    });
    const total = visibleBusinesses.length;
    const items = Object.keys(counts).map((key) => ({
      key,
      label: ZONE_LABELS[key],
      color: ZONE_COLORS[key],
      count: counts[key],
      percent: total > 0 ? Math.round((counts[key] / total) * 100) : 0,
    }));
    return { total, items };
  }, [visibleBusinesses]);
  const naicsDistribution = useMemo(() => {
    if (!visibleBusinesses.length) return { total: 0, items: [] };
    const bucketCounts = {};
    const bucketDescriptions = {};
    let unknownCount = 0;
    const prefixDirectory = naicsPrefixDirectoryRef.current || {};
    visibleBusinesses.forEach((biz) => {
      const code = getPrimaryNaicsCode(biz);
      if (code && code.length >= 2) {
        const prefix = code.slice(0, 2);
        bucketCounts[prefix] = (bucketCounts[prefix] || 0) + 1;
        if (!bucketDescriptions[prefix]) {
          const directoryDescription = prefixDirectory[prefix];
          const businessDescription =
            biz?.naicsDescription ||
            biz?.naicS01Description ||
            biz?.naicsDescription ||
            "";
          bucketDescriptions[prefix] =
            directoryDescription || businessDescription || "";
        }
      } else {
        unknownCount += 1;
      }
    });
    const sortedEntries = Object.keys(bucketCounts)
      .map((prefix) => ({
        key: prefix,
        label: bucketDescriptions[prefix]
          ? `${bucketDescriptions[prefix]} (${prefix})`
          : `NAICS ${prefix}`,
        count: bucketCounts[prefix],
      }))
      .sort((a, b) => b.count - a.count);
    const MAX_SEGMENTS = 5;
    let displayed = sortedEntries.slice(0, MAX_SEGMENTS);
    const remaining = sortedEntries.slice(MAX_SEGMENTS);
    const otherCount = remaining.reduce((sum, entry) => sum + entry.count, 0);
    const items = [...displayed];
    if (otherCount > 0) {
      items.push({ key: "other", label: "Other NAICS", count: otherCount });
    }
    if (unknownCount > 0) {
      items.push({ key: "unknown", label: "Unknown", count: unknownCount });
    }
    const total = visibleBusinesses.length;
    const withMeta = items.map((item, index) => ({
      ...item,
      color: NAICS_COLOR_PALETTE[index % NAICS_COLOR_PALETTE.length],
      percent: total > 0 ? Math.round((item.count / total) * 100) : 0,
    }));
    return { total, items: withMeta };
  }, [visibleBusinesses, naicsDirectoryVersion]);
  const categoryDistribution = useMemo(() => {
    if (!visibleBusinesses.length) {
      return { totalAmount: 0, totalCount: 0, items: [] };
    }
    const aggregates = new Map();
    visibleBusinesses.forEach((biz) => {
      const creditData = buildCreditDisplayData(biz, taxCreditData);
      const amountByCategory = new Map();
      creditData.entries.forEach((entry) => {
        const key = entry.categoryKey;
        if (!key) return;
        const amount =
          typeof entry.amount === "number" && !Number.isNaN(entry.amount)
            ? entry.amount
            : 0;
        amountByCategory.set(
          key,
          (amountByCategory.get(key) || 0) + amount
        );
      });
      if (
        creditData.categoryBreakdown &&
        typeof creditData.categoryBreakdown === "object"
      ) {
        CATEGORY_EXPORT_COLUMNS.forEach((column) => {
          const amount = creditData.categoryBreakdown[column.key];
          if (typeof amount === "number" && amount > 0) {
            const key = buildCategoryKey(column.label);
            if (key && !amountByCategory.has(key)) {
              amountByCategory.set(key, amount);
            }
          }
        });
      }
      const categories = collectBusinessCategoryKeys(biz, taxCreditData);
      categories.forEach(({ key, label }) => {
        if (!key) return;
        if (categoryFilterSet && categoryFilterSet.size) {
          if (!categoryFilterSet.has(key)) return;
        }
        const aggregate = aggregates.get(key) || {
          key,
          label,
          amount: 0,
          count: 0,
        };
        aggregate.label = label;
        aggregate.count += 1;
        const contribution = amountByCategory.get(key);
        if (typeof contribution === "number") {
          aggregate.amount += contribution;
        }
        aggregates.set(key, aggregate);
      });
    });
    const entries = Array.from(aggregates.values()).filter(
      (item) => item.amount > 0 || item.count > 0
    );
    if (!entries.length) {
      return { totalAmount: 0, totalCount: 0, items: [] };
    }
    entries.sort((a, b) => {
      if (b.amount !== a.amount) return b.amount - a.amount;
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    });
    const totalAmount = entries.reduce((sum, item) => sum + item.amount, 0);
    const totalCount = entries.reduce((sum, item) => sum + item.count, 0);
    const hasCategoryAmounts = totalAmount > 0;
    const withMeta = entries.map((item, index) => ({
      ...item,
      color: NAICS_COLOR_PALETTE[index % NAICS_COLOR_PALETTE.length],
      percent: hasCategoryAmounts
        ? Math.round(((item.amount || 0) / totalAmount) * 100)
        : totalCount > 0
        ? Math.round((item.count / totalCount) * 100)
        : 0,
    }));
    return { totalAmount, totalCount, items: withMeta };
  }, [categoryFilterSet, taxCreditData, visibleBusinesses]);
  const formatCreditTotal = useCallback(
    (value) =>
      typeof value === "number" && !Number.isNaN(value)
        ? value.toLocaleString(undefined, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          })
        : "$0",
    []
  );
  const formatPercentAsDecimal = useCallback((value) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return "0.00";
    }
    return value.toFixed(2);
  }, []);
  const renderGraphView = () => {
    if (!visibleBusinesses.length) {
      return (
        <div className={styles.graphEmpty}>
          No businesses to visualize. Adjust your filters or load more results.
        </div>
      );
    }
    const totalAmount = Math.max(graphSummary.amounts.total, 0);
    const hasPieData = totalAmount > 0;
    const legend = [
      {
        key: TAX_CREDIT_FILTERS.FEDERAL_WOTC,
        label: "Federal WOTC",
        color: "#1a9c4b",
        amount: graphSummary.amounts.federalWotc,
      },
      {
        key: TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC,
        label: "Federal (Non-WOTC)",
        color: "#0ea5e9",
        amount: graphSummary.amounts.federalNonWotc,
      },
      {
        key: TAX_CREDIT_FILTERS.STATE,
        label: "State Credits",
        color: "#4c6ef5",
        amount: graphSummary.amounts.state,
      },
    ].map((item) => ({
      ...item,
      percent: hasPieData ? (item.amount / totalAmount) * 100 : 0,
    }));
    const pieBackground = hasPieData
      ? buildPieBackgroundFromSegments(legend)
      : "#e4e7f1";
    const graphCardBuilders = [
      () => (
        <div className={styles.graphCard}>
          <div className={styles.graphCardTitle}>Credit Mix</div>
          {hasPieData ? (
            <div className={`${styles.pieChartWrapper} ${styles.pieChartWrapperStacked}`}>
              <div
                className={styles.pieChart}
                style={{ background: pieBackground }}
              >
                <span className={styles.pieChartLabel}>
                  {formatCreditTotal(totalAmount)}
                </span>
              </div>
              <div className={styles.pieLegend}>
                {legend.map((item) => (
                  <div key={item.key} className={styles.pieLegendItem}>
                    <span
                      className={styles.pieLegendColor}
                      style={{ backgroundColor: item.color }}
                    />
                    <span>
                      {item.label} •{" "}
                      <span className={styles.pieLegendValue}>
                        {formatCreditTotal(item.amount)}
                      </span>{" "}
                      ({formatPercentAsDecimal(item.percent)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.graphNoData}>
              No projected credit totals available yet.
            </div>
          )}
        </div>
      ),
      () => (
        <div className={styles.graphCard}>
          <div className={styles.graphCardTitle}>Category Mix</div>
          {categoryDistribution.items.length > 0 ? (
            <div className={`${styles.pieChartWrapper} ${styles.pieChartWrapperStacked}`}>
              <div
                className={styles.pieChart}
                style={{
                  background: buildPieBackgroundFromSegments(
                    categoryDistribution.items
                  ),
                }}
              >
                {categoryDistribution.totalAmount > 0 && (
                  <span className={styles.pieChartLabel}>
                    {formatCreditTotal(categoryDistribution.totalAmount)}
                  </span>
                )}
              </div>
              <div className={styles.pieLegend}>
                {(() => {
                  const hasCategoryAmounts =
                    categoryDistribution.totalAmount > 0;
                  return categoryDistribution.items.map((item) => (
                    <div key={item.key} className={styles.pieLegendItem}>
                      <span
                        className={styles.pieLegendColor}
                        style={{ backgroundColor: item.color }}
                      />
                      <span>
                        {item.label} •{" "}
                        <span className={styles.pieLegendValue}>
                          {hasCategoryAmounts
                            ? formatCreditTotal(item.amount || 0)
                            : item.count}
                        </span>{" "}
                        ({formatPercentAsDecimal(item.percent)}%)
                      </span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          ) : (
            <div className={styles.graphNoData}>
              No category data available yet.
            </div>
          )}
        </div>
      ),
      () => (
        <div className={styles.graphCard}>
          <div className={styles.graphCardTitle}>Zone Distribution</div>
          {zoneDistribution.total > 0 ? (
            <div className={`${styles.pieChartWrapper} ${styles.pieChartWrapperStacked}`}>
              <div
                className={styles.pieChart}
                style={{
                  background: buildPieBackgroundFromSegments(
                    zoneDistribution.items
                  ),
                }}
              >
                <span className={styles.pieChartLabel}>
                  {zoneDistribution.total}
                  <span className={styles.pieChartLabelSecondary}>
                    Businesses
                  </span>
                </span>
              </div>
              <div className={styles.pieLegend}>
                {zoneDistribution.items.map((item) => (
                  <div key={item.key} className={styles.pieLegendItem}>
                    <span
                      className={styles.pieLegendColor}
                      style={{ backgroundColor: item.color }}
                    />
                    <span>
                      {item.label} •{" "}
                      <span className={styles.pieLegendValue}>
                        {item.count}
                      </span>{" "}
                      ({formatPercentAsDecimal(item.percent)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.graphNoData}>
              No businesses available yet.
            </div>
          )}
        </div>
      ),
      () => (
        <div className={styles.graphCard}>
          <div className={styles.graphCardTitle}>Industry Mix</div>
          {naicsDistribution.items.length > 0 ? (
            <div className={`${styles.pieChartWrapper} ${styles.pieChartWrapperStacked}`}>
              <div
                className={styles.pieChart}
                style={{
                  background: buildPieBackgroundFromSegments(
                    naicsDistribution.items
                  ),
                }}
              >
                <span className={styles.pieChartLabel}>
                  {naicsDistribution.total}
                  <span className={styles.pieChartLabelSecondary}>
                    Businesses
                  </span>
                </span>
              </div>
              <div className={styles.pieLegend}>
                {naicsDistribution.items.map((item) => (
                  <div key={item.key} className={styles.pieLegendItem}>
                    <span
                      className={styles.pieLegendColor}
                      style={{ backgroundColor: item.color }}
                    />
                    <span>
                      {item.label} •{" "}
                      <span className={styles.pieLegendValue}>
                        {item.count}
                      </span>{" "}
                      ({formatPercentAsDecimal(item.percent)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.graphNoData}>
              No NAICS data available yet.
            </div>
          )}
        </div>
      ),
    ];
    const effectiveCardsPerSlide = Math.max(
      1,
      Math.min(cardsPerSlide, graphCardBuilders.length)
    );
    const graphSlides = [];
    for (
      let i = 0;
      i < graphCardBuilders.length;
      i += effectiveCardsPerSlide
    ) {
      graphSlides.push(
        graphCardBuilders.slice(i, i + effectiveCardsPerSlide)
      );
    }
    const maxIndex = Math.max(graphSlides.length - 1, 0);
    const safeGraphSlideIndex = Math.min(graphSlideIndex, maxIndex);
    const currentSlide = graphSlides[safeGraphSlideIndex] || [];
    const totalSlides = graphSlides.length || 1;
    return (
      <div className={styles.graphView} ref={graphViewRef}>
        <div className={styles.graphSummary}>
          <div>Total Businesses: {graphSummary.totalBusinesses}</div>
          <div>Filtered View: {resolveCreditFilterLabel(activeCreditFilter)}</div>
        </div>
        <div className={styles.graphCarousel}>
          <button
            type="button"
            className={styles.graphArrow}
            onClick={() =>
              setGraphSlideIndex((prev) => Math.max(prev - 1, 0))
            }
            disabled={safeGraphSlideIndex === 0}
            aria-label="Previous graph"
          >
            ‹
          </button>
          <div className={styles.graphCardWrapper}>
            {currentSlide.map((builder, idx) => (
              <div
                key={`graph-card-${safeGraphSlideIndex}-${idx}`}
                className={styles.graphCardContainer}
              >
                {builder()}
              </div>
            ))}
          </div>
          <button
            type="button"
            className={styles.graphArrow}
            onClick={() =>
              setGraphSlideIndex((prev) => Math.min(prev + 1, maxIndex))
            }
            disabled={safeGraphSlideIndex === maxIndex}
            aria-label="Next graph"
          >
            ›
          </button>
        </div>
        <div className={styles.graphPagination}>
          {Math.min(safeGraphSlideIndex + 1, totalSlides)} / {totalSlides}
        </div>
      </div>
    );
  };
  const handleNaicsFilterChange = useCallback((value) => {
    if (value === "" || NAICS_INPUT_PATTERN.test(value)) {
      setNaicsFilter(value);
    }
  }, []);
  const handleCategoryFilterToggle = useCallback((categoryKey) => {
    if (!categoryKey) return;
    setCreditCategoryFilter((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      if (next.includes(categoryKey)) {
        return next.filter((value) => value !== categoryKey);
      }
      next.push(categoryKey);
      return next;
    });
  }, []);
  const handleResetFilters = useCallback(() => {
    setEmpowermentFilter(EMPOWERMENT_FILTER_VALUES.ALL);
    setNaicsFilter("");
    setTaxCreditFilter(TAX_CREDIT_FILTERS.ALL);
    setCreditCategoryFilter([]);
    setFilterMenuOpen(false);
  }, []);
  const handleFilterToggleClick = useCallback(() => {
    if (filtersLocked) return;
    setFilterMenuOpen((prev) => !prev);
  }, [filtersLocked]);
  const empowermentFilterOptions = [
    { value: EMPOWERMENT_FILTER_VALUES.ALL, label: "All Zones" },
    {
      value: EMPOWERMENT_FILTER_VALUES.EMPOWERMENT,
      label: "Federal Empowerment Zone",
    },
    {
      value: EMPOWERMENT_ZONE_TYPES.RURAL,
      label: "Rural Renewal Counties",
    },
    { value: EMPOWERMENT_ZONE_TYPES.NONE, label: "None" },
  ];
  const taxCreditFilterOptions = [
    {
      value: TAX_CREDIT_FILTERS.ALL,
      label: resolveCreditFilterLabel(TAX_CREDIT_FILTERS.ALL),
    },
    {
      value: TAX_CREDIT_FILTERS.FEDERAL_WOTC,
      label: resolveCreditFilterLabel(TAX_CREDIT_FILTERS.FEDERAL_WOTC),
    },
    {
      value: TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC,
      label: resolveCreditFilterLabel(TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC),
    },
    {
      value: TAX_CREDIT_FILTERS.STATE,
      label: resolveCreditFilterLabel(TAX_CREDIT_FILTERS.STATE),
    },
  ];
  useEffect(() => {
    if (filtersLocked && isFilterMenuOpen) {
      setFilterMenuOpen(false);
    }
  }, [filtersLocked, isFilterMenuOpen]);
  useEffect(() => {
    if (!shouldHydrateFromCache || !cachedCreditMapData || resultsCacheHydratedRef.current) {
      return;
    }
    resultsCacheHydratedRef.current = true;
    setBusinesses(cachedCreditMapData.businesses || []);
    setUploadStatus(Boolean(cachedCreditMapData.uploadStatus));
    setShowMap(Boolean(cachedCreditMapData.showMap));
    setShowUpload(
      typeof cachedCreditMapData.showUpload === "boolean"
        ? cachedCreditMapData.showUpload
        : true
    );
    const cachedModeValue = cachedCreditMapData.uploadMode;
    if (
      cachedModeValue &&
      Object.values(UPLOAD_MODES).includes(cachedModeValue)
    ) {
      setUploadMode(cachedModeValue);
    } else {
      setUploadMode(UPLOAD_MODES.TEMPLATE);
    }
    setIsPrecalculatedSession(
      Boolean(cachedCreditMapData.isPrecalculatedSession)
    );
    setPrecalculatedEditEnabled(
      Boolean(cachedCreditMapData.precalculatedEditEnabled)
    );
    setUploadedFileName(cachedCreditMapData.uploadedFileName || "");
    setSelectedTaxcredits(cachedCreditMapData.selectedTaxcredits || []);
    setSelectionTrayCollapsed(Boolean(cachedCreditMapData.isSelectionTrayCollapsed));
    const cachedFilterValue =
      cachedCreditMapData.empowermentFilter ||
      cachedCreditMapData.zoneFilter ||
      EMPOWERMENT_FILTER_VALUES.ALL;
    setEmpowermentFilter(
      allowedZoneFilterValues.includes(cachedFilterValue)
        ? cachedFilterValue
        : EMPOWERMENT_FILTER_VALUES.ALL
    );
    setNaicsFilter(cachedCreditMapData.naicsFilter || "");
    const cachedTaxFilter = cachedCreditMapData.taxCreditFilter;
    if (
      cachedTaxFilter &&
      (Object.values(TAX_CREDIT_FILTERS).includes(cachedTaxFilter) ||
        cachedTaxFilter === "federal")
    ) {
      setTaxCreditFilter(
        cachedTaxFilter === "federal"
          ? TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC
          : cachedTaxFilter
      );
    } else {
      const cachedScopeValue = cachedCreditMapData.creditTotalsScope;
      setTaxCreditFilter(
        cachedScopeValue &&
          (CREDIT_SCOPE_VALUE_SET.has(cachedScopeValue) ||
            cachedScopeValue === "federal")
          ? resolveFilterFromScope(cachedScopeValue)
          : TAX_CREDIT_FILTERS.ALL
      );
    }
    setIsResultsReady(Boolean(cachedCreditMapData.isResultsReady));
    setDisplayedResultsCount(cachedCreditMapData.displayedResultsCount || 0);
    setTaxCreditData(cachedCreditMapData.taxCreditData || {});
    setAnnualHiresDrafts(cachedCreditMapData.annualHiresDrafts || {});
    setAnnualHiresSaving(cachedCreditMapData.annualHiresSaving || {});
    setVisibleCount(
      cachedCreditMapData.visibleCount || INITIAL_VISIBLE_COUNT
    );
    setLoadedLimit(
      cachedCreditMapData.loadedLimit || INITIAL_LOADED_LIMIT
    );
    setHasQueuedRows(Boolean(cachedCreditMapData.hasQueuedRows));
    setIsProcessingQueuedRows(
      Boolean(cachedCreditMapData.isProcessingQueuedRows)
    );
    setViewMode(cachedCreditMapData.viewMode || "map");
    setActiveImportSessionId(
      cachedImportSessionId || cachedCreditMapData.importSessionId || null
    );
    if (Array.isArray(cachedCreditMapData.failedRows)) {
      setFailedRows(cachedCreditMapData.failedRows);
    }
  }, [
    shouldHydrateFromCache,
    cachedCreditMapData,
    cachedImportSessionId,
  ]);

  const selectedBusinessIds = useMemo(() => {
    if (!Array.isArray(selectedTaxcredits)) return [];
    return selectedTaxcredits
      .map((item) => resolveBusinessIdentifier(item))
      .filter((id) => id !== null && id !== undefined);
  }, [selectedTaxcredits]);
  const isPrecalculatedMode = uploadMode === UPLOAD_MODES.PRECALCULATED;
  const uploadInputId = isPrecalculatedMode
    ? "upload_input_precalculated"
    : "upload_input_template";

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!isValidUploadFile(file)) {
      dispatch(
        showToast({
          type: "error",
          message: "Please upload Excel or CSV files only",
        })
      );
      event.target.value = "";
      return;
    }

    try {
      const worksheet = await loadWorksheetFromFile(file);
      if (!worksheet) {
        throw new Error("Worksheet not found");
      }
      let rowCount = 0;
      worksheet.eachRow((row) => {
        const isNonEmpty = row.values.some((cell) => cell !== "");
        if (isNonEmpty) {
          rowCount++;
        }
      });
      if (rowCount <= 1) {
        dispatch(
          showToast({
            type: "error",
            message: "Please upload a file with valid data",
          })
        );
        event.target.value = "";
        return;
      }

      if (uploadMode === UPLOAD_MODES.PRECALCULATED) {
        const { headerLookup } = resolvePrecalculatedHeaderLookup(worksheet);
        if (!hasValidPrecalculatedHeaders(headerLookup)) {
          dispatch(
            showToast({
              type: "error",
              message:
                "Use the Excel file that was downloaded from Generate a Credit Map to load pre-calculated results.",
            })
          );
          event.target.value = "";
          return;
        }
      }

      setSelectedFile(file);
      setIsResultsReady(false);
      setUploadedFileName(file.name);
      setUploadStatus(false);
      setIsPrecalculatedSession(false);
      setPrecalculatedEditEnabled(false);
    } catch (err) {
      console.error("Error validating uploaded file:", err);
      dispatch(
        showToast({
          type: "error",
          message:
            uploadMode === UPLOAD_MODES.PRECALCULATED
              ? "Unable to read the pre-calculated results file. Please try another download."
              : "Please upload a valid template file",
        })
      );
    }

    event.target.value = "";
  };

  const handleUploadModeChange = useCallback(
    (mode) => {
      if (!Object.values(UPLOAD_MODES).includes(mode) || mode === uploadMode) {
        return;
      }
      setUploadMode(mode);
      setSelectedFile(null);
      setUploadedFileName("");
      setUploadStatus(false);
      setIsPrecalculatedSession(false);
      setPrecalculatedEditEnabled(false);
    },
    [uploadMode]
  );

  const processRow = useCallback(
    async ({ rowNumber, values }) => {
      const [
        _,
        companyName,
        annualHiresOverride,
        streetAddress,
        city,
        state,
        zipCode,
        businessType,
        naicsColRaw,
      ] = values;
      const normalizedZipForSearch = normalizeZipDigits(zipCode);

      const validationError = validateRowData({
        companyName,
        annualHiresOverride,
        streetAddress,
        city,
        state,
        zipCode,
      });
      if (validationError) {
        const failedId = `failed-${rowNumber}`;
        setFailedRows((prev) => {
          if (prev.some((r) => r.id === failedId)) return prev;
          return [
            ...prev,
            {
              id: failedId,
              rowNumber,
              businessName: companyName || "",
              streetAddress: streetAddress || "",
              city: city || "",
              state: state || "",
              zipCode: zipCode || "",
              annualHires: annualHiresOverride || "",
              businessType: businessType || "",
              naicsCode: "",
              errorMessage: validationError,
            },
          ];
        });
        return;
      }

      const searchParams = {
        BusinessName: companyName,
        UseBusinessWildCardSearch: true,
        minEmployeeCount: null,
        maxEmployeeCount: null,
      };
      if (streetAddress && String(streetAddress).trim() !== "")
        searchParams.StreetAddress = String(streetAddress).trim();
      if (city && String(city).trim() !== "")
        searchParams.City = String(city).trim();
      if (state && String(state).trim() !== "")
        searchParams.State = String(state).trim();
      if (normalizedZipForSearch) {
        searchParams.Zip = normalizedZipForSearch;
      }
      const {
        businessType: parsedBusinessType,
        naicsCode: uploadNaicsCodeRaw,
      } = extractBusinessTypeMetadata(businessType);
      const naicsFromCol = naicsColRaw ? normalizeNaicsDigits(String(naicsColRaw).trim()) : null;
      const uploadNaicsCode = naicsFromCol || normalizeNaicsDigits(uploadNaicsCodeRaw);
      if (parsedBusinessType) {
        searchParams.BusinessType = parsedBusinessType;
      }

      try {
        const response = await axiosInstance.get(
          `/businessSearch/ManualBusinessSearch`,
          {
            params: searchParams,
          }
        );
        const responseData = Array.isArray(response.data)
          ? response.data
          : [];

        if (responseData.length === 0) {
          const failedId = `failed-${rowNumber}`;
          setFailedRows((prev) => {
            if (prev.some((r) => r.id === failedId)) return prev;
            return [
              ...prev,
              {
                id: failedId,
                rowNumber,
                businessName: companyName || "",
                streetAddress: streetAddress || "",
                city: city || "",
                state: state || "",
                zipCode: zipCode || "",
                annualHires: annualHiresOverride || "",
                businessType: businessType || "",
                naicsCode: uploadNaicsCode || "",
                errorMessage: "No matching business found.",
              },
            ];
          });
          return;
        }

        const entry = responseData[0];
        const prospectBusinessId = entry.prospectBusinessId;
        const stateFipsValue = pickApiValue(entry, [
          "stateFips",
          "stateFIPS",
          "StateFIPS",
          "StateFips",
        ]);
        const countyFipsValue = pickApiValue(entry, [
          "countyFips",
          "countyFIPS",
          "CountyFIPS",
          "CountyFips",
        ]);
        const versionValue = pickApiValue(entry, ["version", "Version"]);

        const taxCreditResponse = await axiosInstance.post(
          "/taxCredits/lookup/v2",
          [
            {
              prospectBusinessId: prospectBusinessId,
              annualHiresOverride: Number(annualHiresOverride) || undefined,
            },
          ]
        );

        const businessObj = {
          ...entry,
          lat: entry.latitude ? Number(entry.latitude) : undefined,
          lng: entry.longitude ? Number(entry.longitude) : undefined,
          businessName: entry.businessName,
          state: entry.state,
          stateFips: stateFipsValue ?? entry.stateFips ?? entry.stateFIPS ?? entry.StateFIPS ?? null,
          countyFips: countyFipsValue ?? entry.countyFips ?? entry.countyFIPS ?? entry.CountyFIPS ?? null,
          version: versionValue ?? entry.version ?? entry.Version ?? null,
          prospectBusinessId,
          annualHires: Number(annualHiresOverride) || entry.annualHires,
          taxCredits: taxCreditResponse.data,
          isSelected: false,
          preferredNaics: uploadNaicsCode || entry.naics || entry.primarySIC,
        };

        if (uploadNaicsCode) {
          businessObj.preferredNaics = uploadNaicsCode;
          if (!businessObj.naics) {
            businessObj.naics = uploadNaicsCode;
          }
          if (!businessObj.naicS01) {
            businessObj.naicS01 = uploadNaicsCode;
          }
        }
        let resolvedNaicsDescription =
          businessObj.naicsDescription || businessObj.naicS01Description || null;
        if (!resolvedNaicsDescription && uploadNaicsCode) {
          const naicsDetails = await resolveNaicsDetails(uploadNaicsCode);
          if (naicsDetails?.description) {
            resolvedNaicsDescription = naicsDetails.description;
            businessObj.naicsDescription = naicsDetails.description;
            businessObj.naicS01Description = naicsDetails.description;
          }
        }
        if (resolvedNaicsDescription) {
          businessObj.preferredNaicsDescription = resolvedNaicsDescription;
        }


        setBusinesses((prevBusinesses) =>
          mergeBusinessesByBusinessId(prevBusinesses, [businessObj])
        );

        if (
          prospectBusinessId &&
          Array.isArray(businessObj.taxCredits) &&
          businessObj.taxCredits.length > 0
        ) {
          const matched = businessObj.taxCredits[0];
          const projectedCredit =
            matched?.totalProjectedTaxCreditAmount ?? null;
          const availableCredits = Array.isArray(matched?.availableTaxCredits)
            ? matched.availableTaxCredits
            : [];
          const eligibleCredits = availableCredits
            .map((c) => c?.name)
            .filter(Boolean);
          setTaxCreditData((prev) => ({
            ...prev,
            [prospectBusinessId]: {
              projectedCredit,
              eligibleCredits,
              availableCredits,
              raw: matched,
            },
          }));
        }
      } catch (error) {
        console.error(`Error processing Row ${rowNumber}:`, error);
        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (typeof error?.response?.data === "string" && error.response.data) ||
          error?.message ||
          "Unexpected error while fetching business data.";
        const failedId = `failed-${rowNumber}`;
        setFailedRows((prev) => {
          if (prev.some((r) => r.id === failedId)) return prev;
          return [
            ...prev,
            {
              id: failedId,
              rowNumber,
              businessName: companyName || "",
              streetAddress: streetAddress || "",
              city: city || "",
              state: state || "",
              zipCode: zipCode || "",
              annualHires: annualHiresOverride || "",
              businessType: businessType || "",
              naicsCode: uploadNaicsCode || "",
              errorMessage: apiMessage,
            },
          ];
        });
      }
    },
    [dispatch, resolveNaicsDetails]
  );

  const processRowBatch = useCallback(
    async (rows) => {
      if (!Array.isArray(rows) || !rows.length) return;
      for (let i = 0; i < rows.length; i += MAX_CONCURRENT_LOOKUPS) {
        const batch = rows.slice(i, i + MAX_CONCURRENT_LOOKUPS);
        await Promise.all(batch.map((row) => processRow(row)));
      }
    },
    [processRow]
  );

  const handleRetryFailedRow = useCallback(
    async (failedRow) => {
      const { id, businessName, annualHires, businessType, naicsCode: failedNaicsCode } = failedRow;
      const edits = failedRowEdits[id] || {};
      const streetAddress = edits.streetAddress ?? failedRow.streetAddress;
      const city = edits.city ?? failedRow.city;
      const stateVal = edits.state ?? failedRow.state;
      const zipCode = edits.zipCode ?? failedRow.zipCode;

      // Validate fields before calling the API
      const trimmedZip = (zipCode || "").trim();
      if (trimmedZip) {
        const digitsOnly = trimmedZip.replace(/\D/g, "");
        const effectiveZip = digitsOnly.length > 0 && digitsOnly.length < 5
          ? digitsOnly.padStart(5, "0")
          : trimmedZip;
        if (!/^\d{5}(-\d{4})?$/.test(effectiveZip)) {
          setFailedRows((prev) =>
            prev.map((r) =>
              r.id === id
                ? { ...r, errorMessage: "Zip code is invalid — enter a 5-digit (12345) or 9-digit (12345-6789) zip code." }
                : r
            )
          );
          return;
        }
      }

      setFailedRowRetrying((prev) => ({ ...prev, [id]: true }));
      try {
        const normalizedZip = normalizeZipDigits(zipCode);
        const searchParams = {
          BusinessName: businessName,
          UseBusinessWildCardSearch: true,
          minEmployeeCount: null,
          maxEmployeeCount: null,
        };
        if (streetAddress) searchParams.StreetAddress = String(streetAddress).trim();
        if (city) searchParams.City = String(city).trim();
        if (stateVal) searchParams.State = String(stateVal).trim();
        if (normalizedZip) searchParams.Zip = normalizedZip;

        const { businessType: parsedBusinessType, naicsCode: uploadNaicsCodeRaw } =
          extractBusinessTypeMetadata(businessType);
        const uploadNaicsCode = failedNaicsCode || normalizeNaicsDigits(uploadNaicsCodeRaw);
        if (parsedBusinessType) searchParams.BusinessType = parsedBusinessType;

        const response = await axiosInstance.get("/businessSearch/ManualBusinessSearch", {
          params: searchParams,
        });
        const responseData = Array.isArray(response.data) ? response.data : [];

        if (responseData.length === 0) {
          setFailedRows((prev) =>
            prev.map((r) =>
              r.id === id ? { ...r, errorMessage: "No matching business found." } : r
            )
          );
          return;
        }

        const entry = responseData[0];
        const prospectBusinessId = entry.prospectBusinessId;
        const stateFipsValue = pickApiValue(entry, ["stateFips", "stateFIPS", "StateFIPS", "StateFips"]);
        const countyFipsValue = pickApiValue(entry, ["countyFips", "countyFIPS", "CountyFIPS", "CountyFips"]);
        const versionValue = pickApiValue(entry, ["version", "Version"]);

        const taxCreditResponse = await axiosInstance.post("/taxCredits/lookup/v2", [
          {
            prospectBusinessId,
            annualHiresOverride: Number(annualHires) || undefined,
          },
        ]);

        const businessObj = {
          ...entry,
          lat: entry.latitude ? Number(entry.latitude) : undefined,
          lng: entry.longitude ? Number(entry.longitude) : undefined,
          businessName: entry.businessName,
          state: entry.state,
          stateFips: stateFipsValue ?? entry.stateFips ?? entry.stateFIPS ?? null,
          countyFips: countyFipsValue ?? entry.countyFips ?? entry.countyFIPS ?? null,
          version: versionValue ?? entry.version ?? entry.Version ?? null,
          prospectBusinessId,
          annualHires: Number(annualHires) || entry.annualHires,
          taxCredits: taxCreditResponse.data,
          isSelected: false,
          preferredNaics: uploadNaicsCode || entry.naics || entry.primarySIC,
        };

        if (uploadNaicsCode) {
          businessObj.preferredNaics = uploadNaicsCode;
          if (!businessObj.naics) businessObj.naics = uploadNaicsCode;
          if (!businessObj.naicS01) businessObj.naicS01 = uploadNaicsCode;
        }

        let resolvedNaicsDescription =
          businessObj.naicsDescription || businessObj.naicS01Description || null;
        if (!resolvedNaicsDescription && uploadNaicsCode) {
          const naicsDetails = await resolveNaicsDetails(uploadNaicsCode);
          if (naicsDetails?.description) {
            resolvedNaicsDescription = naicsDetails.description;
            businessObj.naicsDescription = naicsDetails.description;
            businessObj.naicS01Description = naicsDetails.description;
          }
        }
        if (resolvedNaicsDescription) {
          businessObj.preferredNaicsDescription = resolvedNaicsDescription;
        }

        setBusinesses((prev) => mergeBusinessesByBusinessId(prev, [businessObj]));

        if (prospectBusinessId && Array.isArray(businessObj.taxCredits) && businessObj.taxCredits.length > 0) {
          const matched = businessObj.taxCredits[0];
          const projectedCredit = matched?.totalProjectedTaxCreditAmount ?? null;
          const availableCredits = Array.isArray(matched?.availableTaxCredits) ? matched.availableTaxCredits : [];
          setTaxCreditData((prev) => ({
            ...prev,
            [prospectBusinessId]: {
              projectedCredit,
              eligibleCredits: availableCredits.map((c) => c?.name).filter(Boolean),
              availableCredits,
              raw: matched,
            },
          }));
        }

        setFailedRows((prev) => prev.filter((r) => r.id !== id));
        setFailedRowEdits((prev) => { const next = { ...prev }; delete next[id]; return next; });
        setFailedRowEditingId((prev) => (prev === id ? null : prev));
      } catch (error) {
        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (typeof error?.response?.data === "string" && error.response.data) ||
          error?.message ||
          "Unexpected error.";
        const editsAtSave = failedRowEdits[id] || {};
        setFailedRows((prev) =>
          prev.map((r) =>
            r.id === id
              ? {
                  ...r,
                  streetAddress: editsAtSave.streetAddress ?? r.streetAddress,
                  city: editsAtSave.city ?? r.city,
                  state: editsAtSave.state ?? r.state,
                  zipCode: editsAtSave.zipCode ?? r.zipCode,
                  errorMessage: apiMessage,
                }
              : r
          )
        );
        setFailedRowEdits((prev) => { const next = { ...prev }; delete next[id]; return next; });
        setFailedRowEditingId((prev) => (prev === id ? null : prev));
      } finally {
        setFailedRowRetrying((prev) => { const next = { ...prev }; delete next[id]; return next; });
      }
    },
    [failedRowEdits, resolveNaicsDetails]
  );

  const processNextRowChunk = useCallback(
    async (count) => {
      if (processingRowsRef.current) return false;
      if (!pendingRowsRef.current.length) {
        setHasQueuedRows(false);
        return false;
      }
      processingRowsRef.current = true;
      setIsProcessingQueuedRows(true);
      const rows = pendingRowsRef.current.splice(0, count);
      try {
        await processRowBatch(rows);
      } finally {
        processingRowsRef.current = false;
        setIsProcessingQueuedRows(false);
        setHasQueuedRows(pendingRowsRef.current.length > 0);
      }
      return rows.length > 0;
    },
    [processRowBatch]
  );

  const startRowProcessing = useCallback(
    (count) => {
      if (!pendingRowsRef.current.length) return null;
      if (processingRowsRef.current && rowChunkPromiseRef.current) {
        return rowChunkPromiseRef.current;
      }
      if (processingRowsRef.current) {
        return null;
      }
      const promise = processNextRowChunk(count);
      if (!promise || typeof promise.then !== "function") {
        return promise;
      }
      const wrapped = promise.finally(() => {
        if (rowChunkPromiseRef.current === wrapped) {
          rowChunkPromiseRef.current = null;
        }
      });
      rowChunkPromiseRef.current = wrapped;
      return wrapped;
    },
    [processNextRowChunk]
  );

  const handleGenerateCreditMap = () => {
    if (!selectedFile) return;
    if (uploadMode === UPLOAD_MODES.PRECALCULATED) {
      handlePrecalculatedUpload();
    } else {
      handleUpload();
    }
  };

  // Download template: include BusinessType instead of businessRangeStart/businessRangeEnd
  const handleDownload = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");
    worksheet.addRow([
      "Company Name",
      "Annual number of hires",
      "Street Address",
      "City",
      "State",
      "Zip Code",
      "BusinessType",
      "NAICS Code",
    ]);
    const fileName = "company_info_template.xlsx";
    workbook.xlsx
      .writeBuffer()
      .then((buffer) => {
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error("Error generating Excel file:", error);
      });
  };

  const fetchTaxCreditForBusiness = useCallback(
    async (business, overrideAnnualHires, options = {}) => {
      if (!business?.prospectBusinessId) return null;
      if (isPrecalculatedSession && !precalculatedEditEnabled) return null;
      const { force } = options;
      const id = business.prospectBusinessId;
      if (!isPrecalculatedSession && !force && taxCreditData[id]) {
        return taxCreditData[id];
      }
      const resolveAnnualHiresValue = () => {
        if (
          typeof overrideAnnualHires === "number" &&
          !Number.isNaN(overrideAnnualHires)
        ) {
          return overrideAnnualHires;
        }
        if (
          typeof business.annualHiresOverride === "number" &&
          !Number.isNaN(business.annualHiresOverride)
        ) {
          return business.annualHiresOverride;
        }
        if (
          typeof business.annualHires === "number" &&
          !Number.isNaN(business.annualHires)
        ) {
          return business.annualHires;
        }
        return undefined;
      };
      const resolvedAnnualHires = resolveAnnualHiresValue();
      const parseCoordinate = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      const payload = isPrecalculatedSession
        ? [
            {
              prospectBusinessId: id,
              businessName: business.businessName || "",
              businessType:
                business.businessType ||
                business.preferredNaicsDescription ||
                business.naicsDescription ||
                business.naicS01Description ||
                business.siC01Description ||
                "",
              lat: parseCoordinate(business.lat) ?? parseCoordinate(business.latitude) ?? 0,
              lng: parseCoordinate(business.lng) ?? parseCoordinate(business.longitude) ?? 0,
              fips: business.fipsCode || business.fips || "",
              countyFIPS: business.countyFips || business.countyFIPS || "",
              stateFIPS: business.stateFips || business.stateFIPS || "",
              censusTract: business.censusTract || "",
              naics:
                business.preferredNaics ||
                business.naics ||
                business.naicS01 ||
                "",
              annualHires:
                typeof resolvedAnnualHires === "number" && !Number.isNaN(resolvedAnnualHires)
                  ? resolvedAnnualHires
                  : 0,
            },
          ]
        : [
            {
              prospectBusinessId: id,
              annualHiresOverride: resolvedAnnualHires,
            },
          ];
      const endpoint = isPrecalculatedSession
        ? "/taxCredits/lookup/v2/precomputed"
        : "/taxCredits/lookup/v2";
      setTaxCreditLoading((prev) => ({ ...prev, [id]: true }));
      try {
        const resp = await axiosInstance.post(endpoint, payload);
        const normalizedResponse = Array.isArray(resp.data)
          ? resp.data
          : [resp.data];
        const matched =
          normalizedResponse.find((r) => r?.prospectBusinessId === id) ||
          normalizedResponse[0];
        const projectedCredit =
          matched?.totalProjectedTaxCreditAmount ??
          matched?.projectedCredit ??
          null;
        const availableCredits = Array.isArray(matched?.availableTaxCredits)
          ? matched.availableTaxCredits
          : [];
        const eligibleCredits = availableCredits
          .map((c) => c?.name)
          .filter(Boolean);
        const creditObj = {
          projectedCredit,
          eligibleCredits,
          availableCredits,
          raw: matched,
        };
        setTaxCreditData((prev) => ({ ...prev, [id]: creditObj }));
        const normalizedTaxCredits =
          matched && Array.isArray(matched) ? matched : matched ? [matched] : [];
        setBusinesses((prev) =>
          prev.map((b) =>
            b.prospectBusinessId === id
              ? {
                  ...b,
                  annualHires:
                    typeof resolvedAnnualHires === "number"
                      ? resolvedAnnualHires
                      : b.annualHires,
                  annualHiresOverride:
                    typeof resolvedAnnualHires === "number"
                      ? resolvedAnnualHires
                      : b.annualHiresOverride,
                  projectedCredit:
                    typeof projectedCredit === "number"
                      ? projectedCredit
                      : b.projectedCredit,
                  totalProjectedTaxCreditAmount:
                    typeof projectedCredit === "number"
                      ? projectedCredit
                      : b.totalProjectedTaxCreditAmount,
                  taxCredits:
                    normalizedTaxCredits.length > 0
                      ? normalizedTaxCredits
                      : b.taxCredits,
                  eligibleCredits:
                    eligibleCredits.length > 0
                      ? eligibleCredits
                      : b.eligibleCredits,
                }
              : b
          )
        );
        return creditObj;
      } catch (err) {
        return null;
      } finally {
        setTaxCreditLoading((prev) => ({ ...prev, [id]: false }));
      }
    },
    [taxCreditData, isPrecalculatedSession, precalculatedEditEnabled]
  );

  useEffect(() => {
    if (isPrecalculatedSession) return;
    if (!Array.isArray(businesses) || businesses.length === 0) return;
    businesses.forEach((biz) => {
      if (!biz) return;
      const id = biz.prospectBusinessId;
      if (!id) return;
      const hasTaxCredits = Array.isArray(biz.taxCredits) && biz.taxCredits.length > 0;
      const cached = taxCreditData[id];
      if (!hasTaxCredits && !cached && !taxCreditLoading[id]) {
        fetchTaxCreditForBusiness(biz);
      } else if (!hasTaxCredits && cached) {
        setBusinesses((prev) =>
          prev.map((b) =>
            b.prospectBusinessId === id
              ? {
                  ...b,
                  taxCredits: [
                    {
                      totalProjectedTaxCreditAmount: cached.projectedCredit,
                      availableTaxCredits:
                        Array.isArray(cached.availableCredits) && cached.availableCredits.length
                          ? cached.availableCredits
                          : (cached.eligibleCredits || []).map((n) =>
                              typeof n === "string" ? { name: n } : n
                            ),
                    },
                  ],
                }
              : b
          )
        );
      }
    });
  }, [
    businesses,
    taxCreditData,
    taxCreditLoading,
    fetchTaxCreditForBusiness,
    isPrecalculatedSession,
  ]);

  useEffect(() => {
    const selectedFromBusinesses = safeBusinesses.filter(
      (biz) => biz.isSelected
    );
    setSelectedTaxcredits((prev) => {
      if (areSelectionsEqual(prev, selectedFromBusinesses)) {
        return prev;
      }
      return selectedFromBusinesses;
    });
  }, [safeBusinesses]);

  useEffect(() => {
    if (!isResultsReady) {
      if (displayedResultsCount !== 0) {
        setDisplayedResultsCount(0);
      }
      return;
    }
    const actualCount = totalFilteredCount;
    const awaitingAdditionalRows = hasQueuedRows || isProcessingQueuedRows;
    if (!isEmpowermentFilterAll || hasAdditionalFilters) {
      if (displayedResultsCount !== actualCount) {
        setDisplayedResultsCount(actualCount);
      }
      return;
    }
    if (actualCount === 0) {
      if (displayedResultsCount !== 0) {
        setDisplayedResultsCount(0);
      }
      return;
    }
    if (actualCount < displayedResultsCount) {
      setDisplayedResultsCount(actualCount);
      return;
    }
    if (!awaitingAdditionalRows) {
      if (displayedResultsCount !== actualCount) {
        setDisplayedResultsCount(actualCount);
      }
      return;
    }
    if (!hasMoreResults) {
      if (displayedResultsCount !== actualCount) {
        setDisplayedResultsCount(actualCount);
      }
      return;
    }
    if (actualCount < INITIAL_VISIBLE_COUNT) {
      if (displayedResultsCount !== actualCount) {
        setDisplayedResultsCount(actualCount);
      }
      return;
    }
    const extra = actualCount - INITIAL_VISIBLE_COUNT;
    const completedChunks = Math.floor(extra / LOAD_MORE_INCREMENT);
    const chunkedCount =
      INITIAL_VISIBLE_COUNT + completedChunks * LOAD_MORE_INCREMENT;
    const boundedCount = Math.min(chunkedCount, actualCount);
    if (displayedResultsCount !== boundedCount) {
      setDisplayedResultsCount(boundedCount);
    }
  }, [
    totalFilteredCount,
    isResultsReady,
    hasMoreResults,
    hasAdditionalFilters,
    isEmpowermentFilterAll,
    displayedResultsCount,
    hasQueuedRows,
    isProcessingQueuedRows,
  ]);

  useEffect(() => {
    if (!isResultsReady || !totalFilteredCount) {
      return;
    }
    const target = Math.min(
      filteredBusinesses.length,
      INITIAL_VISIBLE_COUNT
    );
    setVisibleCount((prev) => (prev >= target ? prev : target));
    setLoadedLimit((prev) => (prev >= target ? prev : target));
  }, [totalFilteredCount, isResultsReady]);

  // Main upload logic using ManualBusinessSearch; lookup as before!
  const handleUpload = async () => {
    if (!selectedFile) return;
    beginNewImportSession();
    setIsPrecalculatedSession(false);
    setPrecalculatedEditEnabled(false);

    setLoadedLimit(INITIAL_LOADED_LIMIT);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
    setProcessing(true);
    setIsResultsReady(false);
    setUploadedFileName(selectedFile.name);
    setDisplayedResultsCount(0);
    pendingRowsRef.current = [];
    setHasQueuedRows(false);
    setIsProcessingQueuedRows(false);
    rowChunkPromiseRef.current = null;

    try {
      const worksheet = await loadWorksheetFromFile(selectedFile);
      if (!worksheet) {
        throw new Error("Worksheet not found");
      }
      const headerRow = worksheet.getRow(1);
      const isTemplateValid = MANUAL_SEARCH_TEMPLATE_HEADERS.every(
        (expectedHeader, idx) =>
          doesHeaderMatch(headerRow.getCell(idx + 1).value, expectedHeader)
      );
      if (!isTemplateValid) {
        dispatch(
          showToast({
            type: "error",
            message: "Please use correct excel template",
          })
        );
        setUploadStatus(false);
        setIsResultsReady(false);
        setSelectedFile(null);
        setUploadedFileName("");
        return;
      }

      const rowsToProcess = [];
      const totalRows = worksheet.rowCount;
      for (let rowNumber = 2; rowNumber <= totalRows; rowNumber++) {
        const row = worksheet.getRow(rowNumber);
        const isNonEmpty =
          Array.isArray(row.values) &&
          row.values.some(
            (cell) => cell !== "" && typeof cell !== "undefined"
          );
        if (!isNonEmpty) continue;
        rowsToProcess.push({ rowNumber, values: row.values });
      }

      pendingRowsRef.current = rowsToProcess;
      setHasQueuedRows(pendingRowsRef.current.length > 0);

      const initialChunkProcessed = await processNextRowChunk(
        INITIAL_VISIBLE_COUNT
      );

      if (!initialChunkProcessed) {
        pendingRowsRef.current = [];
        setHasQueuedRows(false);
        dispatch(
          showToast({
            type: "error",
            message:
              "No valid business rows were found in the uploaded file. Please verify the template and try again.",
          })
        );
        setUploadStatus(false);
        setShowMap(false);
        setIsResultsReady(false);
        return;
      }

      setUploadStatus(true);
      setShowMap(true);
      setIsResultsReady(true);

      const prefetchTarget = Math.min(
        PREFETCH_CHUNK_SIZE,
        pendingRowsRef.current.length
      );
      if (prefetchTarget > 0) {
        startRowProcessing(prefetchTarget);
      }
    } catch (error) {
      pendingRowsRef.current = [];
      setHasQueuedRows(false);
      console.error("Error during upload processing:", error);
      dispatch(
        showToast({
          type: "error",
          message: "An unexpected error occurred. Please try again",
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const handlePrecalculatedUpload = async () => {
    if (!selectedFile) return;
    beginNewImportSession();

    setProcessing(true);
    setIsResultsReady(false);
    setDisplayedResultsCount(0);
    pendingRowsRef.current = [];
    setHasQueuedRows(false);
    setIsProcessingQueuedRows(false);
    rowChunkPromiseRef.current = null;
    setIsLoadingMoreRows(false);

    try {
      const worksheet = await loadWorksheetFromFile(selectedFile);
      if (!worksheet) {
        throw new Error("Worksheet not found");
      }
      const {
        businesses: businessesFromSheet,
        hasExplicitProspectIds,
      } = buildBusinessesFromPrecalculatedWorksheet(worksheet);
      if (!Array.isArray(businessesFromSheet) || !businessesFromSheet.length) {
        throw new Error(
          "No businesses were found in the uploaded pre-calculated Excel file."
        );
      }

      setBusinesses(businessesFromSheet);
      setSelectedTaxcredits([]);
      setUploadStatus(true);
      setShowMap(true);
      setShowUpload(false);
      setIsResultsReady(true);
      setIsPrecalculatedSession(true);
      setPrecalculatedEditEnabled(Boolean(hasExplicitProspectIds));
      setLoadedLimit(businessesFromSheet.length);
      setVisibleCount(businessesFromSheet.length);
      setDisplayedResultsCount(businessesFromSheet.length);
      setTaxCreditData({});
      setTaxCreditLoading({});
      setAnnualHiresDrafts({});
      setAnnualHiresSaving({});
      setViewMode("map");
      processingRowsRef.current = false;
      dispatch(
        showToast({
          type: "success",
          message:
            "Pre-calculated results loaded. All insights were read directly from your spreadsheet.",
        })
      );
    } catch (error) {
      console.error("Error processing pre-calculated upload:", error);
      setUploadStatus(false);
      setShowMap(false);
      setIsPrecalculatedSession(false);
      setPrecalculatedEditEnabled(false);
      dispatch(
        showToast({
          type: "error",
          message:
            error?.message ||
            "Unable to process the pre-calculated Excel file. Please verify the download and try again.",
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerate = () => {
    console.log("Generating credit map...");
  };
  const handleGoBackToUpload = () => {
    dispatch(clearCreditMapResults());
    setActiveImportSessionId(null);
    setUploadStatus(false);
    setProcessing(true);
    setProcessing(false);
    setShowMap(false);
    setShowUpload(true);
    setSelectedFile(null);
    setUploadedFileName("");
    setBusinesses([]);
    setFailedRows([]);
    setFailedRowEdits({});
    setFailedRowRetrying({});
    setSelectedTaxcredits([]);
    setTaxCreditData({});
    setTaxCreditLoading({});
    setIsPrecalculatedSession(false);
    setPrecalculatedEditEnabled(false);
    setEmpowermentFilter(EMPOWERMENT_FILTER_VALUES.ALL);
    setNaicsFilter("");
    setTaxCreditFilter(TAX_CREDIT_FILTERS.ALL);
    setLoadedLimit(INITIAL_LOADED_LIMIT);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
    setDisplayedResultsCount(0);
    pendingRowsRef.current = [];
    processingRowsRef.current = false;
    rowChunkPromiseRef.current = null;
    setHasQueuedRows(false);
    setIsProcessingQueuedRows(false);
    setIsLoadingMoreRows(false);
    setViewMode("map");
  };

  const handleCancel = () => {
    setProcessing(false);
    setShowUpload(true);
  };
  const handleDelete = () => {
    setUploadedFileName("");
    setSelectedFile(null);
  };
  const handleViewModeChange = useCallback(
    (mode) => {
      setViewMode(mode);
    },
    []
  );

  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadMenuRef = useRef(null);

  useEffect(() => {
    if (!downloadMenuOpen) return;
    const handler = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [downloadMenuOpen]);

  const handleDownloadMapView = useCallback(() => {
    downloadMapAsHtml({ businesses: limitedBusinesses, dispatch, showToastFn: showToast });
  }, [limitedBusinesses, dispatch]);

  const handleDownloadMapViewAsPdf = useCallback(() => {
    downloadMapAsPdf({ businesses: limitedBusinesses, dispatch, showToastFn: showToast });
  }, [limitedBusinesses, dispatch]);

  const buildCurrentGraphData = useCallback(() => {
    if (!visibleBusinesses.length) return null;

    const totalAmount = Math.max(graphSummary.amounts.total, 0);
    const hasPieData = totalAmount > 0;
    const formatUSD = (n) =>
      Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

    const creditSegments = [
      { label: "Federal WOTC", color: "#1a9c4b", amount: graphSummary.amounts.federalWotc },
      { label: "Federal (Non-WOTC)", color: "#0ea5e9", amount: graphSummary.amounts.federalNonWotc },
      { label: "State Credits", color: "#4c6ef5", amount: graphSummary.amounts.state },
    ].map((item) => ({
      ...item,
      percent: hasPieData ? (item.amount / totalAmount) * 100 : 0,
    }));

    const hasCategoryAmounts = categoryDistribution.totalAmount > 0;

    const charts = [
      {
        title: "Credit Mix",
        centerLabel: hasPieData ? formatUSD(totalAmount) : "",
        centerSub: null,
        segments: creditSegments.map((s) => ({ color: s.color, percent: s.percent })),
        legend: creditSegments.map((s) => ({
          label: s.label,
          color: s.color,
          value: formatUSD(s.amount),
          percent: s.percent.toFixed(2) + "%",
        })),
      },
      {
        title: "Category Mix",
        centerLabel: "",
        centerSub: null,
        segments: categoryDistribution.items.map((s) => ({ color: s.color, percent: s.percent })),
        legend: categoryDistribution.items.map((s) => ({
          label: s.label,
          color: s.color,
          value: hasCategoryAmounts ? formatUSD(s.amount || 0) : String(s.count),
          percent: (s.percent || 0).toFixed(2) + "%",
        })),
      },
      {
        title: "Zone Distribution",
        centerLabel: String(zoneDistribution.total),
        centerSub: "Businesses",
        segments: zoneDistribution.items.map((s) => ({ color: s.color, percent: s.percent })),
        legend: zoneDistribution.items.map((s) => ({
          label: s.label,
          color: s.color,
          value: String(s.count),
          percent: (s.percent || 0).toFixed(2) + "%",
        })),
      },
      {
        title: "Industry Mix",
        centerLabel: String(naicsDistribution.total),
        centerSub: "Businesses",
        segments: naicsDistribution.items.map((s) => ({ color: s.color, percent: s.percent })),
        legend: naicsDistribution.items.map((s) => ({
          label: s.label,
          color: s.color,
          value: String(s.count),
          percent: (s.percent || 0).toFixed(2) + "%",
        })),
      },
    ];

    return {
      totalBusinesses: graphSummary.totalBusinesses,
      filteredView: resolveCreditFilterLabel(activeCreditFilter),
      charts,
    };
  }, [visibleBusinesses, graphSummary, categoryDistribution, zoneDistribution, naicsDistribution, activeCreditFilter]);

  const buildSelectedGraphData = useCallback(() => {
    if (!selectedTaxcredits.length) return null;

    const selectedCreditTotals = computeCreditTypeTotals(selectedTaxcredits, taxCreditData);
    const totalAmount = Math.max(selectedCreditTotals.total, 0);
    const hasPieData = totalAmount > 0;
    const formatUSD = (n) =>
      Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

    const creditSegments = [
      { label: "Federal WOTC", color: "#1a9c4b", amount: selectedCreditTotals.federalWotc },
      { label: "Federal (Non-WOTC)", color: "#0ea5e9", amount: selectedCreditTotals.federalNonWotc },
      { label: "State Credits", color: "#4c6ef5", amount: selectedCreditTotals.state },
    ].map((item) => ({
      ...item,
      percent: hasPieData ? (item.amount / totalAmount) * 100 : 0,
    }));

    // Zone distribution
    const zoneCounts = {
      [EMPOWERMENT_ZONE_TYPES.EMPOWERMENT]: 0,
      [EMPOWERMENT_ZONE_TYPES.RURAL]: 0,
      [EMPOWERMENT_ZONE_TYPES.NONE]: 0,
    };
    selectedTaxcredits.forEach((biz) => {
      const category = getEmpowermentZoneCategory(biz.empowermentZone);
      zoneCounts[category] = (zoneCounts[category] || 0) + 1;
    });
    const zoneTotal = selectedTaxcredits.length;
    const zoneItems = Object.keys(zoneCounts).map((key) => ({
      label: ZONE_LABELS[key],
      color: ZONE_COLORS[key],
      count: zoneCounts[key],
      percent: zoneTotal > 0 ? Math.round((zoneCounts[key] / zoneTotal) * 100) : 0,
    }));

    // NAICS distribution
    const naicsBuckets = {};
    const naicsDescs = {};
    let unknownCount = 0;
    const prefixDirectory = naicsPrefixDirectoryRef.current || {};
    selectedTaxcredits.forEach((biz) => {
      const code = getPrimaryNaicsCode(biz);
      if (code && code.length >= 2) {
        const prefix = code.slice(0, 2);
        naicsBuckets[prefix] = (naicsBuckets[prefix] || 0) + 1;
        if (!naicsDescs[prefix]) {
          naicsDescs[prefix] = prefixDirectory[prefix] || biz?.naicsDescription || biz?.naicS01Description || "";
        }
      } else {
        unknownCount += 1;
      }
    });
    const sortedNaics = Object.keys(naicsBuckets)
      .map((prefix) => ({
        key: prefix,
        label: naicsDescs[prefix] ? `${naicsDescs[prefix]} (${prefix})` : `NAICS ${prefix}`,
        count: naicsBuckets[prefix],
      }))
      .sort((a, b) => b.count - a.count);
    const MAX_SEG = 5;
    const naicsItems = sortedNaics.slice(0, MAX_SEG);
    const naicsOther = sortedNaics.slice(MAX_SEG).reduce((s, e) => s + e.count, 0);
    if (naicsOther > 0) naicsItems.push({ key: "other", label: "Other NAICS", count: naicsOther });
    if (unknownCount > 0) naicsItems.push({ key: "unknown", label: "Unknown", count: unknownCount });
    const naicsWithMeta = naicsItems.map((item, i) => ({
      ...item,
      color: NAICS_COLOR_PALETTE[i % NAICS_COLOR_PALETTE.length],
      percent: zoneTotal > 0 ? Math.round((item.count / zoneTotal) * 100) : 0,
    }));

    // Category distribution
    const aggregates = new Map();
    selectedTaxcredits.forEach((biz) => {
      const creditData = buildCreditDisplayData(biz, taxCreditData);
      const amountByCategory = new Map();
      creditData.entries.forEach((entry) => {
        const key = entry.categoryKey;
        if (!key) return;
        const amount = typeof entry.amount === "number" && !Number.isNaN(entry.amount) ? entry.amount : 0;
        amountByCategory.set(key, (amountByCategory.get(key) || 0) + amount);
      });
      if (creditData.categoryBreakdown && typeof creditData.categoryBreakdown === "object") {
        CATEGORY_EXPORT_COLUMNS.forEach((column) => {
          const amount = creditData.categoryBreakdown[column.key];
          if (typeof amount === "number" && amount > 0) {
            const key = buildCategoryKey(column.label);
            if (key && !amountByCategory.has(key)) amountByCategory.set(key, amount);
          }
        });
      }
      collectBusinessCategoryKeys(biz, taxCreditData).forEach(({ key, label }) => {
        if (!key) return;
        const agg = aggregates.get(key) || { key, label, amount: 0, count: 0 };
        agg.label = label;
        agg.count += 1;
        const contribution = amountByCategory.get(key);
        if (typeof contribution === "number") agg.amount += contribution;
        aggregates.set(key, agg);
      });
    });
    const catEntries = Array.from(aggregates.values())
      .filter((item) => item.amount > 0 || item.count > 0)
      .sort((a, b) => b.amount !== a.amount ? b.amount - a.amount : b.count !== a.count ? b.count - a.count : a.label.localeCompare(b.label));
    const catTotalAmount = catEntries.reduce((s, i) => s + i.amount, 0);
    const catTotalCount = catEntries.reduce((s, i) => s + i.count, 0);
    const hasCatAmounts = catTotalAmount > 0;
    const catItems = catEntries.map((item, i) => ({
      ...item,
      color: NAICS_COLOR_PALETTE[i % NAICS_COLOR_PALETTE.length],
      percent: hasCatAmounts
        ? Math.round(((item.amount || 0) / catTotalAmount) * 100)
        : catTotalCount > 0 ? Math.round((item.count / catTotalCount) * 100) : 0,
    }));

    const charts = [
      {
        title: "Credit Mix",
        centerLabel: hasPieData ? formatUSD(totalAmount) : "",
        centerSub: null,
        segments: creditSegments.map((s) => ({ color: s.color, percent: s.percent })),
        legend: creditSegments.map((s) => ({
          label: s.label,
          color: s.color,
          value: formatUSD(s.amount),
          percent: s.percent.toFixed(2) + "%",
        })),
      },
      {
        title: "Category Mix",
        centerLabel: "",
        centerSub: null,
        segments: catItems.map((s) => ({ color: s.color, percent: s.percent })),
        legend: catItems.map((s) => ({
          label: s.label,
          color: s.color,
          value: hasCatAmounts ? formatUSD(s.amount || 0) : String(s.count),
          percent: (s.percent || 0).toFixed(2) + "%",
        })),
      },
      {
        title: "Zone Distribution",
        centerLabel: String(zoneTotal),
        centerSub: "Businesses",
        segments: zoneItems.map((s) => ({ color: s.color, percent: s.percent })),
        legend: zoneItems.map((s) => ({
          label: s.label,
          color: s.color,
          value: String(s.count),
          percent: (s.percent || 0).toFixed(2) + "%",
        })),
      },
      {
        title: "Industry Mix",
        centerLabel: String(selectedTaxcredits.length),
        centerSub: "Businesses",
        segments: naicsWithMeta.map((s) => ({ color: s.color, percent: s.percent })),
        legend: naicsWithMeta.map((s) => ({
          label: s.label,
          color: s.color,
          value: String(s.count),
          percent: (s.percent || 0).toFixed(2) + "%",
        })),
      },
    ];

    return {
      totalBusinesses: selectedTaxcredits.length,
      filteredView: "Selected Businesses",
      charts,
    };
  }, [selectedTaxcredits, taxCreditData]);

  const handleDownloadGraphView = useCallback(() => {
    const data = buildCurrentGraphData();
    if (!data) return;
    const html = buildGraphHtml(data);
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    saveAs(blob, "credit-graph.html");
  }, [buildCurrentGraphData]);

  const handleDownloadGraphViewAsPdf = useCallback(() => {
    const data = buildCurrentGraphData();
    if (!data) return;
    downloadGraphAsPdf(data);
  }, [buildCurrentGraphData, viewMode]);

  const NavigateTowebsite = (url) => {
    const fullUrl =
      url && (String(url).startsWith("http://") || String(url).startsWith("https://"))
        ? url
        : `https://${url}`;
    window.open(fullUrl, "_blank");
  };

  useEffect(() => {
    if (!selectedTaxcredits.length && isSelectionTrayCollapsed) {
      setSelectionTrayCollapsed(false);
    }
  }, [selectedTaxcredits, isSelectionTrayCollapsed]);

  const handleCheckBoxChange = useCallback(
    (post) => {
      const businessId = resolveBusinessIdentifier(post);
      if (!businessId) return;
      setBusinesses((prev) =>
        prev.map((checkbox) => {
          if (resolveBusinessIdentifier(checkbox) !== businessId) {
            return checkbox;
          }
          return { ...checkbox, isSelected: !checkbox.isSelected };
        })
      );
    },
    [setBusinesses]
  );

  const toggleSelectAllBusinesses = useCallback(() => {
    if (!visibleBusinesses.length) return;
    const visibleIds = new Set(
      visibleBusinesses
        .map((biz) => resolveBusinessIdentifier(biz))
        .filter(Boolean)
    );
    if (!visibleIds.size) return;
    const shouldSelectAll = !allVisibleSelected;
    setBusinesses((prev) =>
      prev.map((biz) => {
        const bizId = resolveBusinessIdentifier(biz);
        if (!visibleIds.has(bizId)) {
          return biz;
        }
        if (biz.isSelected === shouldSelectAll) {
          return biz;
        }
        return { ...biz, isSelected: shouldSelectAll };
      })
    );
  }, [visibleBusinesses, allVisibleSelected]);

  const handleMarkerSelection = useCallback(
    (business) => {
      const businessId = resolveBusinessIdentifier(business);
      if (!businessId) return;
      setBusinesses((prev) =>
        prev.map((item) => {
          if (resolveBusinessIdentifier(item) !== businessId) {
            return item;
          }
          if (item.isSelected) {
            return item;
          }
          return { ...item, isSelected: true };
        })
      );
    },
    [setBusinesses]
  );

  const visibleCountRef = useRef(visibleCount);
  useEffect(() => {
    visibleCountRef.current = visibleCount;
  }, [visibleCount]);

  const revealChunkIfReady = useCallback(
    (allowIncomplete = false) => {
      const total = filteredBusinessesRef.current.length;
      const currentVisible = visibleCountRef.current;
      const remaining = total - currentVisible;
      if (remaining <= 0) return 0;
      if (!allowIncomplete && remaining < LOAD_MORE_INCREMENT) {
        return 0;
      }
      const revealCount = allowIncomplete
        ? remaining
        : Math.min(remaining, LOAD_MORE_INCREMENT);
      if (revealCount <= 0) return 0;
      const newVisible = currentVisible + revealCount;
      visibleCountRef.current = newVisible;
      setVisibleCount(newVisible);
      setLoadedLimit((prev) => {
        if (prev >= newVisible + PREFETCH_CHUNK_SIZE) {
          return prev;
        }
        return newVisible + PREFETCH_CHUNK_SIZE;
      });
      return revealCount;
    },
    [setLoadedLimit, setVisibleCount]
  );

  const handleLoadMoreResults = useCallback(async () => {
    if (!hasMoreResults || isLoadingMoreRows) return;
    const revealedImmediately = revealChunkIfReady(false);
    if (revealedImmediately > 0) {
      if (pendingRowsRef.current.length > 0) {
        startRowProcessing(PREFETCH_CHUNK_SIZE);
      }
      return;
    }
    if (!pendingRowsRef.current.length) {
      revealChunkIfReady(true);
      return;
    }
    setIsLoadingMoreRows(true);
    let shouldAutoRetry = false;
    try {
      const ongoing =
        rowChunkPromiseRef.current ||
        startRowProcessing(LOAD_MORE_INCREMENT);
      const chunkFetched = ongoing ? await ongoing : false;
      await waitForNextFrame();
      const allowIncomplete =
        pendingRowsRef.current.length === 0 && !processingRowsRef.current;
      if (chunkFetched) {
        const revealed = revealChunkIfReady(allowIncomplete);
        if (revealed === 0) {
          shouldAutoRetry = true;
        } else if (revealed >= LOAD_MORE_INCREMENT && pendingRowsRef.current.length > 0) {
          startRowProcessing(PREFETCH_CHUNK_SIZE);
        }
      } else {
        revealChunkIfReady(true);
      }
    } finally {
      setIsLoadingMoreRows(false);
      if (shouldAutoRetry) {
        setTimeout(() => {
          handleLoadMoreResults();
        }, 0);
      }
    }
  }, [
    hasMoreResults,
    isLoadingMoreRows,
    revealChunkIfReady,
    startRowProcessing,
  ]);

  useEffect(() => {
    if (!showMap || !isResultsReady) return;
    if (!hasMoreResults) return;
    if (isLoadingMoreRows) return;
    handleLoadMoreResults();
  }, [
    showMap,
    isResultsReady,
    hasMoreResults,
    isLoadingMoreRows,
    handleLoadMoreResults,
  ]);

  useEffect(() => {
    if (isPrecalculatedSession) return;
    if (!isResultsReady) return;
    if (hasQueuedRows || isProcessingQueuedRows) return;
    const totalAvailable = filteredBusinessesRef.current.length;
    if (totalAvailable === 0) return;
    if (visibleCountRef.current >= totalAvailable) return;
    revealChunkIfReady(true);
  }, [
    isPrecalculatedSession,
    isResultsReady,
    hasQueuedRows,
    isProcessingQueuedRows,
    revealChunkIfReady,
    filteredBusinesses.length,
  ]);

  const canEditBusinessAnnualHires = useCallback(
    (business) => {
      if (!business?.prospectBusinessId) return false;
      if (!isPrecalculatedSession) return true;
      if (!precalculatedEditEnabled) return false;
      return Boolean(business?.precalculatedHasProspectId);
    },
    [isPrecalculatedSession, precalculatedEditEnabled]
  );

  const startEditingAnnualHires = (businessId, currentValue) => {
    const targetBusiness = businesses.find(
      (biz) => biz.prospectBusinessId === businessId
    );
    if (!canEditBusinessAnnualHires(targetBusiness)) {
      dispatch(
        showToast({
          type: "info",
          message:
            "Annual hires can't be edited for this pre-calculated file. Download a fresh report to enable edits.",
        })
      );
      return;
    }
    setAnnualHiresDrafts((prev) => ({
      ...prev,
      [businessId]:
        currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : "",
    }));
  };

  const cancelEditingAnnualHires = (businessId) => {
    setAnnualHiresDrafts((prev) => {
      const { [businessId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleAnnualHiresInputChange = (businessId, value) => {
    setAnnualHiresDrafts((prev) => ({
      ...prev,
      [businessId]: value,
    }));
  };

  const handleAnnualHiresSave = async (business) => {
    const businessId = business?.prospectBusinessId;
    if (!businessId) return;
    const draftValue = annualHiresDrafts[businessId];
    const trimmed =
      typeof draftValue === "string" ? draftValue.trim() : draftValue;
    if (trimmed === "" || trimmed === null || typeof trimmed === "undefined") {
      dispatch(
        showToast({
          type: "error",
          message: "Please enter a valid annual hires value.",
        })
      );
      return;
    }
    const hiresValue = Number(trimmed);
    if (Number.isNaN(hiresValue) || hiresValue < 0) {
      dispatch(
        showToast({
          type: "error",
          message: "Please enter a valid annual hires value.",
        })
      );
      return;
    }
    if (!canEditBusinessAnnualHires(business)) {
      dispatch(
        showToast({
          type: "info",
          message:
            "Annual hires can't be updated for this pre-calculated record.",
        })
      );
      cancelEditingAnnualHires(businessId);
      return;
    }
    setAnnualHiresSaving((prev) => ({ ...prev, [businessId]: true }));
    try {
      await fetchTaxCreditForBusiness(
        { ...business, annualHires: hiresValue, annualHiresOverride: hiresValue },
        hiresValue,
        { force: true }
      );
      setBusinesses((prev) =>
        prev.map((item) =>
          item.prospectBusinessId === businessId
            ? { ...item, annualHires: hiresValue, annualHiresOverride: hiresValue }
            : item
        )
      );
      cancelEditingAnnualHires(businessId);
      dispatch(
        showToast({
          type: "success",
          message: "Annual hires updated.",
        })
      );
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: "Failed to update annual hires. Please try again.",
        })
      );
    } finally {
      setAnnualHiresSaving((prev) => ({ ...prev, [businessId]: false }));
    }
  };

  const truncateCredit = (credit) => {
    if (!credit) return "";
    const words = credit.split(" ");
    return words.length > 2 ? words.slice(0, 2).join(" ") + "..." : credit;
  };

  const buildExcelJsonFromBusinesses = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((item) => {
      const name = stripHtml(item.businessName || "");
      const website = item.url ? `https://${item.url}` : "";
      const category = item.siC01Description || "";
      const addressParts = [
        item.physicalAddress,
        item.physicalCity,
        item.state,
        item.physicalZip,
      ].filter(Boolean);
      const address = addressParts.join(", ");
      const employees =
        typeof item.locationEmployeesCount === "number"
          ? item.locationEmployeesCount
          : "";
      const annualHires =
        typeof item.annualHires === "number" ? item.annualHires : "";

      return {
        Name: name,
        Website: website,
        Category: category,
        Address: address,
        Employees: employees,
        "Annual hires": annualHires,
        Phone: item.phone || "",
        Email: item.email || "",
        "Sales volume": item.salesVolume || "",
      };
    });
  };

  const downloadTaxCreditsAsExcel = async () => {
    if (!selectedTaxcredits.length) {
      return;
    }
    const jsonData = await prepareRequiredJsonToExcelDownload(selectedTaxcredits);
    const heading = resolveVersionHeading(selectedTaxcredits);
    const fileName = buildVersionedFileName(
      "Tax_Credits_Analysis",
      selectedTaxcredits
    );
    convertJsonToExcel(jsonData, fileName, { heading });
  };

  const downloadAllResultsAsExcel = async () => {
    if (!Array.isArray(businesses) || !businesses.length) {
      return;
    }
    const jsonData = await prepareRequiredJsonToExcelDownload(businesses);
    const heading = resolveVersionHeading(businesses);
    const fileName = buildVersionedFileName(
      "All_Tax_Credits_Results",
      businesses
    );
    convertJsonToExcel(jsonData, fileName, { heading });
  };

  const downloadTaxCreditsasPdf = async () => {
    const isSelection = selectedTaxcredits.length > 0;
    const mapBusinesses = isSelection ? selectedTaxcredits : limitedBusinesses;
    const graphData = isSelection ? buildSelectedGraphData() : buildCurrentGraphData();
    if (!graphData) return;
    setIsLoading(true);

    const TEMP_ID = "combined-pdf-preview-content";

    const cleanup = () => {
      document.getElementById(TEMP_ID)?.remove();
    };

    try {
      await waitForNextFrame();

      // Render map + graph canvases in parallel, then bake header into each
      const headerOpts = {
        userName: appUserName,
        logoUrl: canaryLogo,
        date: dayjs().format("M/D/YYYY"),
      };
      const [rawMap, rawGraph] = await Promise.all([
        renderMapCanvas(mapBusinesses),
        Promise.resolve(renderGraphCanvas(graphData)),
      ]);
      const [mapResult, graphResult] = await Promise.all([
        rawMap ? composeWithHeader(rawMap, headerOpts) : Promise.resolve(null),
        rawGraph ? composeWithHeader(rawGraph, headerOpts) : Promise.resolve(null),
      ]);

      const mapDataUrl = mapResult ? mapResult.canvas.toDataURL("image/jpeg", 0.92) : null;
      const graphDataUrl = graphResult ? graphResult.canvas.toDataURL("image/jpeg", 0.92) : null;

      // Get existing business detail HTML (already rendered off-screen)
      const detailEl = document.getElementById("credit-map-pdf-content");
      const detailHtml = isSelection && detailEl ? detailEl.innerHTML : "";

      // Build combined printable HTML — each canvas page is a single <img>, no split-page risk
      const combinedHtml = `
        <style>
          .pdf-canvas-page {
            width: 100%;
            background: #fff;
            page-break-after: always;
            break-after: page;
            box-sizing: border-box;
          }
          .pdf-canvas-page img {
            width: 100%;
            height: auto;
            display: block;
          }
          .pdf-detail-pages { background: #fff; }
        </style>
        ${mapDataUrl ? `<div class="pdf-canvas-page"><img src="${mapDataUrl}" /></div>` : ""}
        ${graphDataUrl ? `<div class="pdf-canvas-page"><img src="${graphDataUrl}" /></div>` : ""}
        <div class="pdf-detail-pages">${detailHtml}</div>
      `;

      // Inject into a temporary off-screen element
      cleanup();
      const tempEl = document.createElement("div");
      tempEl.id = TEMP_ID;
      Object.assign(tempEl.style, {
        position: "absolute",
        left: "-9999px",
        top: "0",
        background: "#fff",
        width: "1024px",
      });
      tempEl.innerHTML = combinedHtml;
      document.body.appendChild(tempEl);

      await openPrintPreviewFromTemplate(TEMP_ID, {
        title: `Tax Credit Analysis — ${dayjs().format("M/D/YYYY")}`,
      });
    } catch (error) {
      if (error?.message?.toLowerCase().includes("print preview was closed")) {
        return;
      }
      dispatch(
        showToast({
          type: "error",
          message: error?.message || "Failed to generate PDF. Please try again",
        })
      );
    } finally {
      cleanup();
      setIsLoading(false);
    }
  };
return (
    <div>
      {showMap && (
        <div
          className=" text-right "
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <LoadingButton
            variant="contained"
            color="primary"
            size="medium"
            onClick={(e) => handleGoBackToUpload(e)}
            className="mt-3"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#FFCC33",
              color: "#000",
              borderColor: "#FFCC33",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#e6b800",
                borderColor: "#e6b800",
                boxShadow: "none",
              },
              "&:active": {
                boxShadow: "none",
              },
              "&:focus": {
                boxShadow: "none",
              },
            }}
          >
            Generate again
          </LoadingButton>
        </div>
      )}
      <div
        className={showMap ? styles.mapContainer : styles.container}
        style={showMap ? { minHeight: 0 } : undefined}
      >
        <header className={styles.header}>{/* Header content */}</header>
        <div className="d-lg-flex d-md-flex">
          {!showMap && (
            <div
              className={`col-lg-6 col-md-6 col-sm-12 ${
                !isMobile ? "px-3" : ""
              }`}
            >
              <div className={`${styles.downloadCard} ${styles.firstcard} `}>
                <h3
                  className={`${styles.card_title} ${styles.textTitleForDocument}`}
                >
                  Download Company Information Template
                </h3>
                <div
                  className={`${styles.card} ${styles.docummentCard} $ gap-3`}
                >
                  <img
                    src={DownloadShape}
                    width="20px"
                    height="20px"
                    alt="download"
                  ></img>

                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleDownload}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      backgroundColor: "#FFCC33",
                      color: "#000",
                      borderColor: "#FFCC33",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#e6b800",
                        borderColor: "#e6b800",
                        boxShadow: "none",
                      },
                      "&:active": {
                        boxShadow: "none",
                      },
                      "&:focus": {
                        boxShadow: "none",
                      },
                    }}
                  >
                    <img
                      src={NavDownloadArrow}
                      width="20px"
                      height="20px"
                      alt="download"
                    ></img>{" "}
                    Download document
                  </LoadingButton>
                </div>
              </div>
            </div>
          )}
          {!showMap && !uploadStatus && (
            <div
              className={`col-lg-6 col-md-6 col-sm-12 ${
                !isMobile ? "px-3" : ""
              }`}
            >
              <div
                className={`${
                  uploadedFileName ? styles.card : styles.downloadCard
                } ${styles.firstcard} ${isMobile ? "border-0 p-0 pt-4" : ""}`}
              >
                <div className={styles.uploadOptionsPanel}>
                  <div className={styles.uploadModeToggle}>
                    {UPLOAD_MODE_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className={`${styles.uploadModeOption} ${
                          uploadMode === option.value
                            ? styles.uploadModeOptionActive
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="credit-map-upload-mode"
                          value={option.value}
                          checked={uploadMode === option.value}
                          onChange={() => handleUploadModeChange(option.value)}
                        />
                        <div className={styles.uploadModeOptionBody}>
                          <div className={styles.uploadModeTitle}>
                            {option.title}
                          </div>
                          <div className={styles.uploadModeHelper}>
                            {option.description}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div
                  className={`${styles.card} ${styles.docummentCardUplod} ${
                    uploadedFileName ? styles.mb12 : ""
                  } gap-3`}
                >
                  <img src={Union} width="20px" height="20px" alt="union"></img>
                  <input
                    key={uploadMode}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onInput={handleFileChange}
                    id={uploadInputId}
                    className={styles.upload_input}
                  />

                  {!processing && (
                    <label
                      htmlFor={uploadInputId}
                      className={styles.upload_label}
                    >
                      <img
                        src={ArrowUpload}
                        width="20px"
                        height="20px"
                        alt="upload"
                      ></img>{" "}
                      Upload
                    </label>
                  )}
                  {processing && (
                    <div className={styles.loader_container}>
                      <div className={styles.loader}></div>
                      <button
                        className={styles.cancel_genreate_button}
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                {uploadedFileName && !processing && (
                  <div className={` ${styles.uploadDocumnetFile} col-12`}>
                    <div style={{ float: "left" }}>
                      <div
                        className={`${styles.card_title} ${
                          isMobile ? styles.ellipsis : ""
                        }`}
                      >
                        <img
                          src={FileIcon}
                          width="40px"
                          height="40px"
                          alt="File"
                          style={{ marginRight: "8px" }}
                        ></img>
                        {uploadedFileName}
                      </div>
                    </div>
                    <div>
                      <img
                        onClick={handleDelete}
                        style={{ float: "right" }}
                        src={DeleteIcon}
                        width="36px"
                        height="36px"
                        alt="Delete"
                      ></img>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {showMap && (
          <div className={styles.viewSection}>
            <div className={styles.viewToggleRow}>
              <div style={{ position: "relative" }} ref={downloadMenuRef}>
                <button
                  type="button"
                  className={`${styles.downloadViewButton}${filtersLocked ? ` ${styles.downloadViewButtonDisabled}` : ""}`}
                  disabled={filtersLocked}
                  onClick={() => setDownloadMenuOpen((v) => !v)}
                >
                  <img src={NavDownloadArrow} alt="" width="14" height="14" />
                  Download ▾
                </button>
                {downloadMenuOpen && !filtersLocked && (
                  <div style={{ position: "absolute", left: 0, top: "calc(100% + 4px)", zIndex: 999, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 170, overflow: "hidden" }}>
                    <button
                      type="button"
                      style={{ display: "block", width: "100%", padding: "10px 16px", textAlign: "left", background: "none", border: "none", borderBottom: "1px solid #f3f4f6", cursor: "pointer", fontSize: 13, color: "#374151" }}
                      onClick={() => { setDownloadMenuOpen(false); (viewMode === "map" ? handleDownloadMapView : handleDownloadGraphView)(); }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                    >
                      Download as HTML
                    </button>
                    <button
                      type="button"
                      style={{ display: "block", width: "100%", padding: "10px 16px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#374151" }}
                      onClick={() => { setDownloadMenuOpen(false); (viewMode === "map" ? handleDownloadMapViewAsPdf : handleDownloadGraphViewAsPdf)(); }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                    >
                      Download as PDF
                    </button>
                  </div>
                )}
              </div>
              <div className={styles.viewToggleGroup}>
                <span className={styles.viewToggleLabel}>View:</span>
                <div className={styles.viewToggleButtons}>
                  <button
                    type="button"
                    className={`${styles.viewToggleButton} ${
                      viewMode === "map" ? styles.viewToggleButtonActive : ""
                    }`}
                    onClick={() => handleViewModeChange("map")}
                    aria-pressed={viewMode === "map"}
                  >
                    Map View
                  </button>
                  <button
                    type="button"
                    className={`${styles.viewToggleButton} ${
                      viewMode === "graph" ? styles.viewToggleButtonActive : ""
                    }`}
                    onClick={() => handleViewModeChange("graph")}
                    aria-pressed={viewMode === "graph"}
                  >
                    Graph View
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.viewSurface}>
              {viewMode === "map" ? (
                <MapView
                  businesses={limitedBusinesses}
                  selectedBusinessIds={selectedBusinessIds}
                  onMarkerSelect={handleMarkerSelection}
                />
              ) : (
                renderGraphView()
              )}
            </div>
          </div>
        )}
        {!showMap && (
          <div
            className={`card-footer d-flex text-right align-self-end gap-3 mt-auto ${
              isMobile ? "px-0 pt-2 w-100" : "px-3"
            }`}
          >
            <LoadingButton
              variant="contained"
              size="medium"
              onClick={(e) => handleGenerateCreditMap(e)}
              disabled={!selectedFile}
              loading={processing}
              className={isMobile ? "w-100" : ""}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#FFCC33",
                color: "#000",
                borderColor: "#FFCC33",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#e6b800",
                  borderColor: "#e6b800",
                  boxShadow: "none",
                },
                "&:active": {
                  boxShadow: "none",
                },
                "&:focus": {
                  boxShadow: "none",
                },
              }}
            >
              {isPrecalculatedMode ? "Load Results" : "Generate"}
            </LoadingButton>
          </div>
        )}
      </div>

      {showMap && (
        <div className="my-4">
          <div className={styles.resultsHeader}>
            <div className={styles.resultsHeaderLeft}>
              <div className={styles.resultsTitleRow}>
                <div className={`font-700 ${isMobile ? "font-16" : "font-32"}`}>
                  All Tax Credit Details
                  {isResultsReady && (
                    <span className={styles.resultsCount}>
                      {" "}
                      ({displayedResultsCount})
                    </span>
                  )}
                </div>
                {isPrecalculatedSession && (
                  <div className={styles.dataSourceBadge}>
                    {isPrecalculatedEditable
                      ? "Pre-calculated file • Editable"
                      : "Pre-calculated file • View only"}
                  </div>
                )}
                <div
                  className={`${styles.filterDropdown} ${
                    isFilterMenuOpen ? styles.filterDropdownOpen : ""
                  } ${filtersLocked ? styles.filterDropdownDisabled : ""}`}
                  ref={filterMenuRef}
                >
                  <button
                    type="button"
                    className={styles.filterToggle}
                    onClick={handleFilterToggleClick}
                    disabled={filtersLocked}
                    aria-haspopup="true"
                    aria-expanded={isFilterMenuOpen}
                  >
                    Filter Results
                    <span className={styles.filterCaret} />
                  </button>
                  {isFilterMenuOpen && (
                    <div className={styles.filterMenu}>
                      <div className={styles.filterSection}>
                        <div className={styles.filterSectionTitle}>
                          Empowerment Zone
                        </div>
                        <div className={styles.filterOptions}>
                          {empowermentFilterOptions.map((option) => (
                            <label
                              key={option.value}
                              className={styles.filterOption}
                            >
                              <input
                                type="radio"
                                name="empowerment-filter"
                                value={option.value}
                                checked={empowermentFilter === option.value}
                                onChange={() => setEmpowermentFilter(option.value)}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className={styles.filterSection}>
                        <div className={styles.filterSectionTitle}>
                          NAICS Filter
                        </div>
                        <input
                          type="text"
                          className={styles.filterInput}
                          value={naicsFilter}
                          onChange={(e) => handleNaicsFilterChange(e.target.value)}
                          placeholder="Enter 2-6 digit NAICS"
                          maxLength={6}
                        />
                        <div
                          className={`${styles.filterHelper} ${
                            isNaicsFilterPending ? styles.filterHelperWarning : ""
                          }`}
                        >
                          {isNaicsFilterPending
                            ? "Enter at least 2 digits to apply this filter."
                            : isNaicsFilterActive
                            ? `Filtering NAICS codes matching ${sanitizedNaicsFilter}.`
                            : "Filter results by a 2-6 digit NAICS code."}
                        </div>
                      </div>
                      <div className={styles.filterSection}>
                        <div className={styles.filterSectionTitle}>
                          Categories
                        </div>
                        {availableCreditCategories.length ? (
                          <div
                            className={`${styles.filterOptions} ${styles.filterOptionsScrollable}`}
                          >
                            {availableCreditCategories.map((category) => (
                              <label
                                key={category.key}
                                className={styles.filterOption}
                              >
                                <input
                                  type="checkbox"
                                  checked={
                                    Array.isArray(creditCategoryFilter) &&
                                    creditCategoryFilter.includes(category.key)
                                  }
                                  onChange={() =>
                                    handleCategoryFilterToggle(category.key)
                                  }
                                />
                                <span>
                                  {category.label}
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className={styles.filterHelper}>
                            Categories will appear after credits are loaded.
                          </div>
                        )}
                      </div>
                      <div className={styles.filterSection}>
                        <div className={styles.filterSectionTitle}>
                          Tax Credit Type
                        </div>
                        <div className={styles.filterOptions}>
                          {taxCreditFilterOptions.map((option) => (
                            <label
                              key={option.value}
                              className={styles.filterOption}
                            >
                              <input
                                type="radio"
                                name="tax-credit-filter"
                                value={option.value}
                                checked={taxCreditFilter === option.value}
                                onChange={() => setTaxCreditFilter(option.value)}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className={styles.filterFooter}>
                        <button
                          type="button"
                          className={styles.filterClear}
                          onClick={handleResetFilters}
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {filtersLocked && (
                <div className={styles.filterStatus}>
                  Filters unlock when all cards finish loading.
                </div>
              )}
              {visibleBusinesses.length > 0 && (
                <div className={styles.creditTotalsRow}>
                  {showFederalWotcChip && (
                    <div className={styles.creditTotalChip}>
                      Federal WOTC:{" "}
                      <span>
                        {formatCreditTotal(creditTotals.federalWotc || 0)}
                      </span>
                    </div>
                  )}
                  {showFederalNonWotcChip && (
                    <div className={styles.creditTotalChip}>
                      Federal (Non-WOTC):{" "}
                      <span>
                        {formatCreditTotal(creditTotals.federalNonWotc || 0)}
                      </span>
                    </div>
                  )}
                  {showStateTotalChip && (
                    <div className={styles.creditTotalChip}>
                      State:{" "}
                      <span>{formatCreditTotal(creditTotals.state || 0)}</span>
                    </div>
                  )}
                  {showGrandTotalChip && (
                    <div
                      className={`${styles.creditTotalChip} ${styles.creditTotalChipAll}`}
                    >
                      Total: <span>{formatCreditTotal(creditTotals.total)}</span>
                    </div>
                  )}
                  {failedRows.length > 0 && (
                    <button
                      type="button"
                      className={styles.errorNotice}
                      onClick={() =>
                        failedSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                      }
                    >
                      ⚠ {failedRows.length}{" "}
                      {failedRows.length === 1 ? "address" : "addresses"} couldn't be resolved —{" "}
                      <span className={styles.errorNoticeLink}>Fix them ↓</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            <Button
              label={
                allVisibleSelected ? "Unselect Visible" : "Select Visible"
              }
              type="button"
              variant="outlined"
              onClick={toggleSelectAllBusinesses}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#D5D8DC",
                color: "#23244c",
                minWidth: 140,
              }}
            />
          </div>
          <hr></hr>
          <div className="row d-lg-flex d-md-flex">
            {visibleBusinesses.map((item, index) => {
              const businessId = item.prospectBusinessId;
              const creditObj =
                (businessId && taxCreditData[businessId]) ||
                extractTaxCreditFields(item);
              const creditDisplayData = buildCreditDisplayData(
                item,
                taxCreditData
              );
              const {
                entries: categoryScopedEntries,
                usedCategoryFallback: usedCategoryFallbackForCategories,
              } = applyCategoryFilterToEntries(
                creditDisplayData.entries,
                creditDisplayData.categoryBreakdown
              );
              const scopedCreditEntries =
                activeCreditFilter === TAX_CREDIT_FILTERS.ALL
                  ? categoryScopedEntries
                  : categoryScopedEntries.filter(
                      (entry) => entry.type === activeCreditFilter
                    );
              const eligibleEntrySource = usedCategoryFallbackForCategories
                ? activeCreditFilter === TAX_CREDIT_FILTERS.ALL
                  ? creditDisplayData.entries
                  : creditDisplayData.entries.filter(
                      (entry) => entry.type === activeCreditFilter
                    )
                : scopedCreditEntries;
              const creditNamesForScope = eligibleEntrySource
                .map((entry) => entry.label)
                .filter(Boolean);
              const loadingTaxCredit =
                businessId && taxCreditLoading[businessId];
              const showProjectedLoading =
                typeof creditObj.projectedCredit === "undefined" &&
                loadingTaxCredit;
              const showEligibleLoading =
                (!creditObj.eligibleCredits ||
                  !Array.isArray(creditObj.eligibleCredits)) &&
                loadingTaxCredit;
              const filteredProjectedCredit = (() => {
                if (categoryFilterSet && categoryFilterSet.size) {
                  return scopedCreditEntries.reduce(
                    (sum, entry) => sum + entry.amount,
                    0
                  );
                }
                if (
                  activeCreditFilter === TAX_CREDIT_FILTERS.FEDERAL_WOTC
                ) {
                  return creditDisplayData.totals.federalWotc;
                }
                if (
                  activeCreditFilter === TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC
                ) {
                  return creditDisplayData.totals.federalNonWotc;
                }
                if (activeCreditFilter === TAX_CREDIT_FILTERS.STATE) {
                  return creditDisplayData.totals.state;
                }
                return creditDisplayData.totals.total;
              })();
              const resolvedProjectedCredit = (() => {
                if (
                  creditDisplayData.hasProjectionValue &&
                  typeof filteredProjectedCredit === "number" &&
                  !Number.isNaN(filteredProjectedCredit)
                ) {
                  return filteredProjectedCredit;
                }
                if (
                  typeof creditObj.projectedCredit !== "undefined" &&
                  creditObj.projectedCredit !== null
                ) {
                  const parsed = parseCreditAmountValue(
                    creditObj.projectedCredit
                  );
                  return parsed !== null ? parsed : null;
                }
                return null;
              })();
              const totalEligibleCredits = creditNamesForScope.length;
              const hasMoreEligibleCredits = totalEligibleCredits > 3;
              const visibleEligibleCredits = hasMoreEligibleCredits
                ? creditNamesForScope.slice(0, 3)
                : creditNamesForScope;
              const extraEligibleCredits = Math.max(
                0,
                totalEligibleCredits - visibleEligibleCredits.length
              );
              const displayAnnualHires =
                item?.annualHires ?? item?.annualHiresOverride;
              const formattedAnnualHires =
                displayAnnualHires !== null &&
                displayAnnualHires !== undefined &&
                displayAnnualHires !== ""
                  ? Number(displayAnnualHires).toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })
                  : null;
              const isEditingAnnualHires = Object.prototype.hasOwnProperty.call(
                annualHiresDrafts,
                businessId
              );
              const draftAnnualHiresValue =
                annualHiresDrafts[businessId] ?? "";
              const savingAnnualHire = annualHiresSaving[businessId];
              const canEditAnnualHires = canEditBusinessAnnualHires(item);
              const naicsDisplay =
                item?.preferredNaics || item?.naics || item?.naicS01 || "_";
                const censusTractDisplay =
                  item?.censusTract ||
                  item?.censusTractCode ||
                  item?.CensusTract ||
                  "_";
              const employeesDisplay =
                typeof item.locationEmployeesCount === "number"
                  ? item.locationEmployeesCount.toLocaleString()
                  : "–";
              const zoneCategory = getEmpowermentZoneCategory(
                item.empowermentZone
              );
              const zoneLabels = getEmpowermentZoneLabels(
                item.empowermentZone
              );
              const hasMultipleZones = zoneLabels.length > 1;
              const cardToneStyle = getZoneCardStyle(
                zoneCategory,
                item.isSelected
              );
              const cardToneClass =
                zoneCategory === EMPOWERMENT_ZONE_TYPES.EMPOWERMENT
                  ? styles.cardEmpowerment
                  : zoneCategory === EMPOWERMENT_ZONE_TYPES.RURAL
                  ? styles.cardRural
                  : "";
              const renderAnnualHiresField = () => {
                if (isEditingAnnualHires) {
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={draftAnnualHiresValue}
                        onChange={(e) =>
                          handleAnnualHiresInputChange(
                            businessId,
                            e.target.value
                          )
                        }
                        style={{
                          width: 130,
                          padding: "4px 6px",
                          borderRadius: 4,
                          border: "1px solid #ccc",
                        }}
                      />
                      <LoadingButton
                        size="small"
                        variant="contained"
                        loading={!!savingAnnualHire}
                        onClick={() => handleAnnualHiresSave(item)}
                        sx={annualHiresSaveButtonSx}
                      >
                        Save
                      </LoadingButton>
                      <button
                        type="button"
                        onClick={() => cancelEditingAnnualHires(businessId)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#0066cc",
                          cursor: "pointer",
                          textDecoration: "underline",
                          padding: 0,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  );
                }
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span className="font-700">
                      {formattedAnnualHires ?? "No Data"}
                    </span>
                    {canEditAnnualHires ? (
                      <button
                        type="button"
                        onClick={() =>
                          startEditingAnnualHires(businessId, displayAnnualHires)
                        }
                        style={{
                          background: "none",
                          border: "none",
                          color: "#0066cc",
                          cursor: "pointer",
                          textDecoration: "underline",
                          padding: 0,
                        }}
                      >
                        Edit
                      </button>
                    ) : (
                      <span className={styles.readOnlyTag}>View only</span>
                    )}
                  </div>
                );
              };

              return (
                <div
                  className={`${styles.businessCardColumn} col-lg-4 col-md-6 col-sm-12 p-right cursor-pointer ${
                    isMobile ? "pb-3" : "pb-4"
                  }`}
                  key={item.prospectBusinessId || index}
                  onClick={() => handleCheckBoxChange(item)}
                >
                    <div
                      className={`card p-3 ${styles.hoverableCard} ${cardToneClass} ${
                        item.isSelected ? " selected-card" : ""
                      }`}
                      style={cardToneStyle}
                    >
                      <div
                        className="d-flex"
                        style={{ alignItems: "flex-start", gap: 8 }}
                      >
                      <div className={styles.cardCheckboxWrapper}>
                        <Checkbox
                          checked={item.isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckBoxChange(item);
                          }}
                          name="subscribe"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="pb-3">
                          <div className={`${styles.title}`}>
                            {stripHtml(item.businessName)}
                          </div>
                          <div className="font-14">
                            {item.physicalAddress}
                            {item.physicalCity ? `, ${item.physicalCity}` : ""}
                            {item.state ? `, ${item.state}` : ""}
                          </div>
                        </div>
                        {zoneLabels.length > 0 && (
                          <div className={styles.empowermentBadgeList}>
                            {zoneLabels.map((label, badgeIndex) => {
                              const badgeCategory = getEmpowermentZoneCategory(
                                label
                              );
                              const badgeClass =
                                badgeCategory ===
                                EMPOWERMENT_ZONE_TYPES.EMPOWERMENT
                                  ? styles.empowermentBadgeEmpowerment
                                  : badgeCategory ===
                                    EMPOWERMENT_ZONE_TYPES.RURAL
                                  ? styles.empowermentBadgeRural
                                  : styles.empowermentBadgeNeutral;
                              const dualClass = hasMultipleZones
                                ? styles.empowermentBadgeDual
                                : "";
                              return (
                                <div
                                  key={`${label}-${badgeIndex}`}
                                  className={`${styles.empowermentBadge} ${badgeClass} ${dualClass}`}
                                >
                                  {label}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {item.siC01Description && (
                          <div className="font-500">Category: {item.siC01Description}</div>
                        )}

                        <div
                          style={{
                            display: "flex",
                            gap: 16,
                            flexWrap: "wrap",
                            fontSize: 14,
                            color: "#727684",
                            marginTop: 8,
                          }}
                        >
                          <span>
                            NAICS Code: <strong>{naicsDisplay}</strong>
                          </span>
                            <span>
                              Census Tract: <strong>{censusTractDisplay}</strong>
                            </span>
                        </div>

                        <div className={` ${!isMobile ? " card-title pt-3" : "pt-2"}`}>
                          <div className="p-right" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <img src={Person} width="20px" height="20px" alt="Person" className="my-auto" />
                              <span className="font-14 font-500 my-auto">
                                Employees:
                                <span className="font-700"> {employeesDisplay}</span>
                              </span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <img src={AnnualHires} width="20px" height="20px" alt="Annual Hires" />
                              <span className="font-14 font-500">Annual hires:</span>
                              {isEditingAnnualHires ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={draftAnnualHiresValue}
                                    onChange={(e) =>
                                      handleAnnualHiresInputChange(
                                        businessId,
                                        e.target.value
                                      )
                                    }
                                    style={{
                                      width: 130,
                                      padding: "4px 6px",
                                      borderRadius: 4,
                                      border: "1px solid #ccc",
                                    }}
                                  />
                                  <LoadingButton
                                    size="small"
                                    variant="contained"
                                    loading={!!savingAnnualHire}
                                    onClick={() => handleAnnualHiresSave(item)}
                                    sx={annualHiresSaveButtonSx}
                                  >
                                    Save
                                  </LoadingButton>
                                  <button
                                    type="button"
                                    onClick={() => cancelEditingAnnualHires(businessId)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#0066cc",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                      padding: 0,
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span className="font-700">
                                    {formattedAnnualHires ?? "No Data"}
                                  </span>
                                  {canEditAnnualHires ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        startEditingAnnualHires(
                                          businessId,
                                          displayAnnualHires
                                        )
                                      }
                                      style={{
                                        background: "none",
                                        border: "none",
                                        color: "#0066cc",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                        padding: 0,
                                      }}
                                    >
                                      Edit
                                    </button>
                                  ) : (
                                    <span className={styles.readOnlyTag}>
                                      View only
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Partition line */}
                        <div className="partition-line" style={{ marginTop: 12, marginBottom: 12 }}></div>

                        {/* Projected and Eligible Credits (like Blog UI) */}
                        {/* Increased font sizes for the label, value and eligible chips only */}
                        <div className="result-card-credits">
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ color: "#888", fontSize: 15 }}>Projected Credit:</div>
                            <div style={{ fontWeight: 700, marginTop: 6, fontSize: 18 }}>
                              {showProjectedLoading
                                ? "Loading..."
                                : resolvedProjectedCredit !== null
                                ? `$${Number(resolvedProjectedCredit).toLocaleString(undefined, {
                                    maximumFractionDigits: 0,
                                  })}`
                                : "—"}
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                color: "#888",
                                fontSize: 15,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <span>Eligible Credits:</span>
                              {!showEligibleLoading && (
                                <span className={styles.eligibleCountValue}>
                                  {totalEligibleCredits}
                                </span>
                              )}
                            </div>
                        <div className={styles.eligibleCreditsWrapper}>
                          {showEligibleLoading ? (
                            <span style={{ color: "#aaa", fontSize: 14 }}>Loading...</span>
                          ) : (
                            <>
                                  {totalEligibleCredits > 0 ? (
                                    <div className={styles.eligibleChipsContainer}>
                                      <div className={styles.eligibleChipsRow}>
                                        {visibleEligibleCredits.map((c, i) => (
                                          <span
                                            key={`${businessId || index}-credit-${i}`}
                                            className="credit-chip"
                                            data-fullcredit={c}
                                            title={c}
                                            style={{
                                              background: "#f4f6fb",
                                              padding: "6px 10px",
                                              borderRadius: 16,
                                              fontSize: 14,
                                            }}
                                          >
                                            {truncateCredit(c)}
                                          </span>
                                        ))}
                                      </div>
                                      {hasMoreEligibleCredits && (
                                        <div className={styles.creditHoverTrigger}>
                                          <button
                                            type="button"
                                            className={styles.creditHoverButton}
                                            aria-label={`Show all ${totalEligibleCredits} eligible credits`}
                                            title={`View all ${totalEligibleCredits} eligible credits`}
                                            onClick={(event) => event.stopPropagation()}
                                          >
                                            +{extraEligibleCredits}
                                          </button>
                                          <div className={styles.creditHoverPanel}>
                                            <div className={styles.creditHoverTitle}>
                                              Eligible Credits ({totalEligibleCredits})
                                            </div>
                                            <div className={styles.creditHoverList}>
                                              {creditNamesForScope.map((credit, hoverIndex) => (
                                                <span
                                                  key={`${businessId || index}-credit-full-${hoverIndex}`}
                                                >
                                                  {credit}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span style={{ color: "#aaa", fontSize: 14 }}>No Data</span>
                                  )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
            })}
          </div>

          {failedRows.length > 0 && (
            <div ref={failedSectionRef}>
              <div className={styles.failedSectionHeader}>
                ⚠ Needs attention — {failedRows.length}{" "}
                {failedRows.length === 1 ? "row" : "rows"}
              </div>
              <div className="row d-lg-flex d-md-flex">
                {failedRows.map((failedRow) => {
                  const isEditing = failedRowEditingId === failedRow.id;
                  const isRetrying = !!failedRowRetrying[failedRow.id];
                  const edits = failedRowEdits[failedRow.id] || {};
                  const errorType = getErrorType(failedRow.errorMessage);
                  const errorHint = getErrorHint(failedRow.errorMessage);
                  const highlightField = getErrorHighlightField(failedRow.errorMessage);

                  const updateEdit = (field, value) =>
                    setFailedRowEdits((prev) => ({
                      ...prev,
                      [failedRow.id]: { ...(prev[failedRow.id] || {}), [field]: value },
                    }));

                  const startEditing = () => {
                    setFailedRowEdits((prev) => ({
                      ...prev,
                      [failedRow.id]: {
                        streetAddress: failedRow.streetAddress,
                        city: failedRow.city,
                        state: failedRow.state,
                        zipCode: failedRow.zipCode,
                      },
                    }));
                    setFailedRowEditingId(failedRow.id);
                  };

                  const cancelEditing = () => {
                    setFailedRowEditingId(null);
                    setFailedRowEdits((prev) => {
                      const next = { ...prev };
                      delete next[failedRow.id];
                      return next;
                    });
                  };

                  const fieldClass = (field) =>
                    `${styles.failedInput}${highlightField === field && !isEditing === false ? ` ${styles.failedInputHighlight}` : ""}`;

                  return (
                    <div key={failedRow.id} className="col-lg-4 col-md-6 col-sm-12 py-2">
                      <div className={`card p-3 ${styles.hoverableCard} ${styles.failedCard}`}>
                        <div className={styles.failedErrorType}>{errorType}</div>

                        <div className={`${styles.title} pt-2`} style={{ fontWeight: 700, fontSize: 15 }}>
                          {failedRow.businessName || "Unknown Business"}
                        </div>

                        {!isEditing ? (
                          <>
                            <div style={{ fontSize: 13, color: "#727684", marginTop: 4 }}>
                              {[failedRow.streetAddress, failedRow.city, failedRow.state, failedRow.zipCode]
                                .filter(Boolean)
                                .join(", ")}
                            </div>
                            <div className={styles.failedHint}>{errorHint}</div>

                            <div className="partition-line" style={{ margin: "10px 0" }} />

                            <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#727684", marginBottom: 8 }}>
                              <span>NAICS Code: <strong>{failedRow.naicsCode || "–"}</strong></span>
                              <span>Census Tract: <strong style={{ color: "#bbb", letterSpacing: "-2px" }}>__</strong></span>
                            </div>

                            <div style={{ marginBottom: 8 }}>
                              <div style={{ color: "#888", fontSize: 13 }}>Projected Credit:</div>
                              <div style={{ fontWeight: 700, fontSize: 13, color: "#bbb" }}>—</div>
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ color: "#888", fontSize: 13 }}>Eligible Credits:</div>
                              <div style={{ fontWeight: 700, fontSize: 13, color: "#bbb" }}>—</div>
                            </div>

                            <button
                              type="button"
                              className={styles.editAddressBtn}
                              onClick={startEditing}
                            >
                              <span>✏</span><span>Edit address</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <div className={styles.failedHint}>{errorHint}</div>
                            <div className="partition-line" style={{ margin: "8px 0" }} />

                            <div className={styles.failedFieldGroup}>
                              <div className={styles.failedFieldLabel}>Street Address</div>
                              <input
                                className={`${styles.failedInput}${highlightField === "streetAddress" ? ` ${styles.failedInputHighlight}` : ""}`}
                                value={edits.streetAddress ?? failedRow.streetAddress}
                                onChange={(e) => updateEdit("streetAddress", e.target.value)}
                                placeholder="Street address"
                                disabled={isRetrying}
                              />
                            </div>
                            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                              <div style={{ flex: 2 }}>
                                <div className={styles.failedFieldLabel}>City</div>
                                <input
                                  className={`${styles.failedInput}${highlightField === "city" ? ` ${styles.failedInputHighlight}` : ""}`}
                                  value={edits.city ?? failedRow.city}
                                  onChange={(e) => updateEdit("city", e.target.value)}
                                  placeholder="City"
                                  disabled={isRetrying}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div className={styles.failedFieldLabel}>State</div>
                                <input
                                  className={`${styles.failedInput}${highlightField === "state" ? ` ${styles.failedInputHighlight}` : ""}`}
                                  value={edits.state ?? failedRow.state}
                                  onChange={(e) => updateEdit("state", e.target.value)}
                                  placeholder="ST"
                                  disabled={isRetrying}
                                  maxLength={2}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div className={styles.failedFieldLabel}>Zip</div>
                                <input
                                  className={`${styles.failedInput}${highlightField === "zipCode" ? ` ${styles.failedInputHighlight}` : ""}`}
                                  value={edits.zipCode ?? failedRow.zipCode}
                                  onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
                                    const formatted = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
                                    updateEdit("zipCode", formatted);
                                  }}
                                  placeholder="12345 or 12345-6789"
                                  disabled={isRetrying}
                                />
                              </div>
                            </div>

                            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                              <LoadingButton
                                size="small"
                                variant="contained"
                                loading={isRetrying}
                                onClick={() => handleRetryFailedRow(failedRow)}
                                sx={{
                                  flex: 1,
                                  borderRadius: "8px",
                                  textTransform: "none",
                                  fontWeight: 600,
                                  background: "#162395",
                                  "&:hover": { background: "#0f1a75" },
                                }}
                              >
                                Save
                              </LoadingButton>
                              <button
                                type="button"
                                className={styles.failedCancelBtn}
                                onClick={cancelEditing}
                                disabled={isRetrying}
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        )}

                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {filteredBusinesses.length > 0 && (
            <div className={styles.loadMoreControls}>
              {hasMoreResults ? (
                <div className={styles.autoLoadIndicator}>
                  <CircularProgress size={20} thickness={5} />
                  <span>Loading more results...</span>
                </div>
              ) : (
                <div className={styles.loadMoreEnd}>No more results</div>
              )}
            </div>
          )}

          {selectedTaxcredits.length > 0 && !isSelectionTrayCollapsed && (
            <div
              className={`fixed-bottom  ${styles.footer} ${
                isMobile ? "p-3" : "p-4"
              }`}
            >
              <div className="d-flex justify-content-between ">
                <div className={`${isMobile ? "col-7" : "col-9"}`}>
                  <div
                    className={`d-flex gap-3 flex-wrap ${
                      isMobile ? " gap-2" : " gap-3"
                    }`}
                  >
                    {selectedTaxcredits.map((item, index) => {
                      return (
                        <div key={item.prospectBusinessId || index}>
                          <Chip
                            sx={{ borderRadius: "8px" }}
                            label={item.businessName}
                            variant="outlined"
                            deleteIcon={<CloseIcon />}
                            onDelete={() => handleCheckBoxChange(item)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div
                  className="d-flex align-self-end gap-3"
                  style={{ float: "right", padding: "4px" }}
                >
                  <LoadingButton
                    variant="contained"
                    size="medium"
                    onClick={downloadTaxCreditsAsExcel}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      backgroundColor: "#FFCC33",
                      color: "#000",
                      borderColor: "#FFCC33",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#e6b800",
                        borderColor: "#e6b800",
                        boxShadow: "none",
                      },
                      "&:active": {
                        boxShadow: "none",
                      },
                      "&:focus": {
                        boxShadow: "none",
                      },
                    }}
                  >
                    {!isMobile && <img src={CSV} width="24px" height="24px" alt="download" />}
                    {isMobile ? "Excel" : "Download Excel"}
                  </LoadingButton>
                  <LoadingButton
                    variant="contained"
                    size="medium"
                    onClick={downloadTaxCreditsasPdf}
                    loading={isLoading}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      backgroundColor: "#FFCC33",
                      color: "#000",
                      borderColor: "#FFCC33",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#e6b800",
                        borderColor: "#e6b800",
                        boxShadow: "none",
                      },
                      "&:active": {
                        boxShadow: "none",
                      },
                      "&:focus": {
                        boxShadow: "none",
                      },
                    }}
                  >
                    {!isMobile && <img src={PDF} width="24px" height="24px" alt="download" />}
                    {isMobile ? "PDF" : "Download PDF"}
                  </LoadingButton>
                  <button
                    type="button"
                    className={styles.selectionClose}
                    onClick={() => setSelectionTrayCollapsed(true)}
                  >
                    <CloseIcon fontSize="small" />
                    <span>Hide Selected</span>
                  </button>
                </div>
              </div>
            </div>
          )}
          {selectedTaxcredits.length > 0 && isSelectionTrayCollapsed && (
            <button
              type="button"
              className={`${styles.selectionToggle} ${
                isMobile ? styles.selectionToggleMobile : ""
              }`}
              onClick={() => setSelectionTrayCollapsed(false)}
            >
              Show selected ({selectedTaxcredits.length})
            </button>
          )}
        </div>
      )}
      {selectedTaxcredits.length > 0 && (
        <div style={{ position: "absolute", left: "-9999px", top: 0, backgroundColor: "#fff" }}>
          <div id="credit-map-pdf-content" style={{ borderRadius: 0, background: "white", paddingBottom: 64 }}>
            {selectedTaxcredits.map((business, index) => {
              const businessPhone = business.phone || "No Data Available";
              const businessEmail = business.email || "No Data Available";

              const projectedCredit =
                Array.isArray(business.taxCredits) && business.taxCredits.length > 0
                  ? business.taxCredits[0]?.totalProjectedTaxCreditAmount
                  : taxCreditData[business.prospectBusinessId]?.projectedCredit ?? null;

              const projectedCreditFormatted = projectedCredit
                ? Number(projectedCredit).toLocaleString(undefined, {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 2,
                  })
                : "No Data";

              const annualSalesFormatted = business.salesVolume
                ? Number(business.salesVolume).toLocaleString(undefined, {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 2,
                  })
                : "No Data";

              return (
                <div key={business.prospectBusinessId || index} style={{ pageBreakBefore: index === 0 ? "auto" : "always", background: "white" }}>
                  <div style={{ background: "#444549", color: "#fff", borderRadius: "0 16px 0 0", padding: "22px 32px 18px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 23, fontWeight: 600, letterSpacing: "-1px" }}>Tax Credit Analysis</div>
                      <div style={{ fontSize: 14, marginTop: 8 }}>Provided by: {appUserName}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "flex-end", minWidth: 120 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1, color: "#fff", marginBottom: 2, textTransform: "uppercase" }}>POWERED BY:</div>
                      <img src={canaryLogo} alt="canary" style={{ height: 28, width: "auto", marginBottom: 4 }} />
                      <div style={{ fontSize: 14, color: "#fff" }}>{dayjs().format("M/D/YYYY")}</div>
                    </div>
                  </div>
                  <div style={{ padding: "2.5rem 2.5rem 1.5rem 2.5rem" }}>
                    <div style={{ color: "#888", fontSize: 14, marginBottom: 10 }}>Business Profile</div>
                    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", rowGap: 8, columnGap: 16, fontSize: 16 }}>
                      <div>Name</div>
                      <div style={{ fontWeight: 600 }}>{business.businessName}</div>
                      <div>Category</div>
                      <div>{business.siC01Description || "No Data"}</div>
                      <div>Location</div>
                      <div>{formatAddress(business)}</div>
                      <div>Employees</div>
                      <div>{typeof business.locationEmployeesCount === "number" ? business.locationEmployeesCount : " _"}</div>
                      <div>Annual hires</div>
                      <div>{business.annualHires ?? "~"}</div>
                      <div>Phone</div>
                      <div>{businessPhone}</div>
                      <div>Email</div>
                      <div>{businessEmail}</div>
                    </div>
                    <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "30px 0 18px 0" }} />
                    <div style={{ color: "#888", fontSize: 14, marginBottom: 10 }}>Tax Credit Analysis</div>
                    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", rowGap: 10, columnGap: 16, fontSize: 16 }}>
                      <div style={{ fontSize: 15 }}>Projected Credit</div>
                      <div style={{ fontWeight: 700, fontSize: 18 }}>{projectedCreditFormatted}</div>
                      <div>Annual Sales</div>
                      <div>{annualSalesFormatted}</div>
                      <div style={{ alignSelf: "start" }}>Credits Identified</div>
                      <div>
                        {getTaxCreditDescriptions(business.taxCredits).length > 0 ? (
                          getTaxCreditDescriptions(business.taxCredits).map((desc, i) => (
                            <div key={i} style={{ marginBottom: 10 }}>
                              <span style={{ fontWeight: 600 }}>{desc.split(":")[0]}</span>
                              <div style={{ fontSize: 15, color: "#333", marginLeft: 2 }}>
                                {desc.split(":").slice(1).join(":").trim()}
                              </div>
                            </div>
                          ))
                        ) : (
                          <span style={{ color: "#aaa" }}>No Data</span>
                        )}
                      </div>
                    </div>
                    <div className="pdf-keep-together" style={{ marginTop: 28, fontSize: 13, color: "#777", breakInside: "avoid", pageBreakInside: "avoid", pageBreakBefore: "avoid", pageBreakAfter: "avoid", position: "relative", zIndex: 2, background: "#fff" }}>
                      <b>Disclaimer:</b> Projected savings are based on estimates related to employee hiring and retention, reflecting adjustments to annual hire data. State credits use a 35% participation rate, while federal credits apply a 20% factor. Actual results may vary.
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditMapGenerator;
